import { DialogService } from "../../services/dialog/dialog.service";

import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Action, Store } from "@ngrx/store";
import { EMPTY, Observable, catchError, combineLatest, filter, finalize, forkJoin, interval, map, mergeMap, of, retry, switchMap, take, takeUntil, takeWhile, tap, withLatestFrom } from "rxjs";
import { RootState, appActions, fromApp, fromProgram, fromRouter, programActions } from "..";
import {
  AdminService,
  AnalyticsService,
  AllowedDomain,
  CampaignDto,
  CouponDto,
  CouponService,
  EngagementFlowProperties,
  EngagementFlowWithProgramEngagementFlowDto,
  EntityTranslation,
  Feature,
  IntegrationService,
  Program,
  ProgramIntegration,
  ProgramService,
  ReorderEntitiesDto,
  RewardDto,
  ShopifyControllerTokenExchangeV1RequestParams,
  SubscriptionInfo,
  ProgramEngagementFlow,
  CustomerActionsService,
  TranslationsService,
  UpsertTranslationDto,
} from "../../services/api";
import { HttpClient, HttpErrorResponse, HttpResponse, HttpStatusCode } from "@angular/common/http";
import { Router } from "@angular/router";
import { AppDialogs, AppRouteParams, AppRoutes, CampaignType, Languages, Platforms } from "../../enums";
import { ToastService } from "../../services/toast/toast.service";
import { TranslateService } from "@ngx-translate/core";

import { ShopifyAppBridgeService } from "../../services/shopify-app-bridge/shopify-app-bridge.service";

import { isDeviceConflictError } from "../../utils/is-device-conflict-error/is-device-conflict-error";
import { concatLatestFrom } from "@ngrx/operators";
import { environment } from "../../../environments/environment";
import { getClaimCodeLink } from "../../utils/get-claim-code-link";
import { getHttpErrorMessage } from "../../utils/http-error-message/http-error-message";
import { getCouponBatchErrorMessage } from "../../utils/coupon-batch-error/coupon-batch-error";
import {
  PROFILE_FORM_SAVE_ERROR_CODES,
  PROFILE_FORM_TRANSLATION_ENTITY_TYPES,
  ProfileFormSaveError,
  ProfileFormSaveErrorCode,
} from "../../utils/profile-form/profile-form";
import { UtilsService } from "../../services/utils/utils.service";
import * as Sentry from "@sentry/angular";
import { DEFAULT_HOLDING_PERIOD_DAYS, INTERCOM_APP_ID, MCP_INTEGRATION_NAME } from "../../constants";
import type { McpSettings } from "../../interfaces";
import { IntercomService } from "../../services/intercom/intercom.service";
import { IntercomPlan } from "../../services/intercom/intercom.types";
import { ENGAGEMENT_DISPLAY_CONFIG_DEFAULTS } from "../../components/engagement-display-config-form/engagement-display-config-form.defaults";
import {
  buildOnboardingTranslationRows,
  getOnboardingLocationCampaignCopy,
  getOnboardingProgramLocales,
  getOnboardingRewardCopy,
  type OnboardingRewardNameKey,
} from "../../services/i18n/customer-copies/onboarding-customer-copy";

type OnboardingRewardInput = Partial<RewardDto> & {
  value: number;
  minimumSpend: number;
  nameKey: OnboardingRewardNameKey;
};

type SaveFromOnboardingFailureResult = ReturnType<typeof programActions.saveFromOnboardingFailure>;

type SaveFromOnboardingResult =
  | { reward: RewardDto; coupon: CouponDto; rewardInput: OnboardingRewardInput }
  | { campaign: CampaignDto }
  | { updatedProgram: Program }
  | { allowedDomain: AllowedDomain }
  | { programEngagementFlow: ProgramEngagementFlow }
  | SaveFromOnboardingFailureResult;

@Injectable()
export class ProgramEffects {
  private readonly programLanguagesInFlight = new Set<string>();

  public getProgram$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.getProgram),
      mergeMap(({ slug, programId, loadingFullScreen }) =>
        this.programService.programControllerGetProgramV1({ programId: programId || slug }).pipe(
          map(program => programActions.getProgramSuccess({ program, loadingFullScreen })),
          catchError((error: HttpErrorResponse) => of(programActions.getProgramFailure({ error, loadingFullScreen }))),
        ),
      ),
    ),
  );

  public readonly getProgramSuccess$: Observable<unknown> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(programActions.getProgramSuccess),
        withLatestFrom(this.store.select(fromApp.selectIframeShop)),
        filter(([, shop]) => !!shop),
        tap(async ([{ program }, shop]) => {
          const sessionToken = await this.shopifyAppBridgeService.getSessionToken();
          this.store.dispatch(programActions.tokenExchange({ programId: program.id, tokenExchange: { shop, sessionToken } }));
        }),
      ),
    { dispatch: false },
  );

  public readonly identifyProgram$: Observable<unknown> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(programActions.getProgramSuccess, programActions.createProgramSuccess),
        withLatestFrom(
          this.store.select(fromProgram.selectSubscriptionInfo),
          this.store.select(fromApp.selectLanguage),
        ),
        tap(([{ program }, subscriptionInfo, language]) => {
          Sentry.setContext("jeri.program", {
            id: program.id,
            slug: program.slug,
          });
          if (window.posthog) {
            const { configurations, contactEmail, ...restOfProgramConfig } = program;
            window.posthog.identify(program.id, { email: contactEmail, ...restOfProgramConfig, environment: environment.name });
          }
          this.bootIntercom(program, subscriptionInfo, language);
        }),
      ),
    { dispatch: false },
  );

  public readonly bootIntercomOnInit$: Observable<unknown> = createEffect(
    () =>
      combineLatest([
        this.store.select(fromProgram.selectProgram).pipe(filter((program): program is Program => !!program)),
        this.store.select(fromProgram.selectSubscriptionInfo),
        this.store.select(fromApp.selectLanguage),
      ]).pipe(
        take(1),
        tap(([program, subscriptionInfo, language]) => {
          this.bootIntercom(program, subscriptionInfo, language);
        }),
      ),
    { dispatch: false },
  );

  public getProgramFailure$: Observable<unknown> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(programActions.getProgramFailure),
        filter(({ error }) => error.status === HttpStatusCode.NotFound),
        tap(() => this.router.navigate([AppRoutes.notFound])),
      ),
    { dispatch: false },
  );

  public createProgram$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.createProgram),
      withLatestFrom(
        this.store.select(fromApp.selectIframeProgramId),
        this.store.select(fromApp.selectIframeIntegrationName),
        this.store.select(fromApp.selectIframeShop),
        this.store.select(fromProgram.selectIntegrations),
        this.store.select(fromProgram.selectFeatures),
      ),
      mergeMap(([{ name, contactEmail, callback }, iframeProgramId, iframeIntegrationName, iframeShop, integrations, features]) => {
        // Creating the program without its integrations would leave it half set up
        const hasIntegrations = ["shopify", "jericommerce"].every(integrationName => integrations?.some(i => i.name === integrationName));
        if (!hasIntegrations) return of(programActions.createProgramFailure({ error: "Integrations not loaded" }));
        // ...and without the features each integration enables
        const hasFeatures = ["users", "loyalty"].every(featureName => features?.some(f => f.name === featureName));
        if (!hasFeatures) return of(programActions.createProgramFailure({ error: "Features not loaded" }));

        return this.adminService.adminControllerCreateProgramV1({
          createProgram: {
            name,
            slug: iframeProgramId,
            integration: iframeIntegrationName,
            contactEmail,
            locationNotifications: true,
            marketingNotifications: true,
          },
        }).pipe(
          mergeMap(program =>
            forkJoin([
              this.adminService.adminControllerAddIntegrationV1({
                programId: program.id,
                createIntegration: {
                  programId: program.id,
                  integrationId: integrations.find(i => i.name === "shopify").id,
                  enabledFeatures: features
                    .filter(f => ["users", "events", "custom-fields"].includes(f.name))
                    .map(f => f.id),
                  properties: { hostName: iframeShop },
                },
              }),
              this.adminService.adminControllerAddIntegrationV1({
                programId: program.id,
                createIntegration: {
                  programId: program.id,
                  integrationId: integrations.find(i => i.name === "jericommerce").id,
                  enabledFeatures: features
                    .filter(f => ["loyalty", "rewards", "segments"].includes(f.name))
                    .map(f => f.id),
                  properties: {
                    hostName: iframeShop,
                    cycleType: "none",
                    balanceType: "points",
                    holdingPeriodDays: DEFAULT_HOLDING_PERIOD_DAYS,
                    applyTierFactorToRegularBalance: false,
                  },
                },
              }),
            ]).pipe(
              mergeMap(async () => {
                const sessionToken = await this.shopifyAppBridgeService.getSessionToken();
                this.store.dispatch(programActions.tokenExchange({ programId: program.id, tokenExchange: { shop: iframeShop, sessionToken } }));
                this.store.dispatch(programActions.getProgramIntegrations({ programId: program.id }));
                return programActions.createProgramSuccess({ program, callback });
              }),
            ),
          ),
          catchError((error: HttpErrorResponse) => of(programActions.createProgramFailure({ error: getHttpErrorMessage(error, "TOAST.error.title") }))),
        );
      }),
    ),
  );

  public createProgramFailure$: Observable<unknown> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(programActions.createProgramFailure),
        tap(() => {
          this.toast.show({
            title: this.translate.instant("ONBOARDING_PAGE.createProgramError"),
            message: "",
            type: "error",
          });
        }),
      ),
    { dispatch: false },
  );

  public updateProgram$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.updateProgram),
      mergeMap(({ params, toastId, callback }) =>
        this.adminService.adminControllerUpdateProgramV1(params).pipe(
          map(program => programActions.updateProgramSuccess({ program, toastId, callback })),
          catchError((error: HttpErrorResponse) => of(programActions.updateProgramFailure({ reason: getHttpErrorMessage(error, "TOAST.error.title") }))),
        ),
      ),
    ),
  );

  public updateProgramSuccess$: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(programActions.updateProgramSuccess),
        filter(({ toastId }) => !!toastId),
        tap(({ toastId }) => {
          this.toast.show(
            {
              title: this.translate.instant("TOAST.save.title"),
              message: this.translate.instant("TOAST.save.message"),
              position: "top-right",
              showAsAbsolute: true,
              duration: 1000,
            },
            toastId,
          );
        }),
      ),
    { dispatch: false },
  );

  public persistProgram$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.persistProgram),
      withLatestFrom(this.store.select(fromProgram.selectProgram), this.store.select(fromProgram.selectProgramSections)),
      mergeMap(([{ callback }, program, sectionDto]) =>
        forkJoin([
          this.adminService.adminControllerUpdateProgramV1({ programId: program.id, updateProgram: program }),
          this.adminService.adminControllerPatchProgramSectionsV1({
            programId: program.id,
            sectionDto: Object.keys(sectionDto).map(tier => sectionDto[tier]).flat(),
          }),
        ]).pipe(
          mergeMap(([program, programSections]) => [
            programActions.persistProgramSuccess({ program, callback }),
            programActions.persistProgramSectionsSuccess({ programSections }),
          ]),
          catchError((error: HttpErrorResponse) =>
            of(programActions.persistProgramFailure({ reason: getHttpErrorMessage(error, "TOAST.error.title") })),
          ),
        ),
      ),
    ),
  );

  public persistProgramSuccess$: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(programActions.persistProgramSuccess),
        tap(() => {
          this.store.dispatch(appActions.setDirtyForm({ dirtyForm: false }));
          this.toast.show({
            title: this.translate.instant("TOAST.save.title"),
            message: this.translate.instant("TOAST.save.message"),
            showAsAbsolute: true,
            position: "bottom-right",
            anchor: "viewport",
          });
        }),
      ),
    { dispatch: false },
  );

  public persistProgramFailure$: Observable<unknown> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(programActions.persistProgramFailure),
        tap(({ reason }) =>
          this.toast.show({
            title: this.translate.instant("TOAST.error.title"),
            message: reason,
            type: "error",
            duration: 5000,
            anchor: "viewport",
            position: "bottom-right",
          }),
        ),
      ),
    { dispatch: false },
  );

  public getProgramLinks$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.getProgramLinks),
      mergeMap(params =>
        this.adminService.adminControllerGetProgramLinksV1({ visibility: "0", programId: params.programId }).pipe(
          map(programLinks => programActions.getProgramLinksSuccess({ programLinks })),
          catchError((error: HttpErrorResponse) => of(programActions.getProgramLinksFailure({ reason: error.error?.response?.message }))),
        ),
      ),
    ),
  );

  public getProgramSections$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.getProgramSections),
      mergeMap(params =>
        this.adminService.adminControllerGetProgramSectionsV1({ visibility: "0", programId: params.programId }).pipe(
          map(programSections => programActions.getProgramSectionsSuccess({ programSections })),
          catchError((error: HttpErrorResponse) =>
            of(programActions.getProgramSectionsFailure({ reason: error.error?.response?.message })),
          ),
        ),
      ),
    ),
  );

  public getProgramTiers$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.getProgramTiers),
      mergeMap(({ programId, xAPPLANGUAGE }) =>
        this.adminService.adminControllerGetProgramTiersV1({ programId, xAPPLANGUAGE }).pipe(
          map(tiers => programActions.getProgramTiersSuccess({ tiers })),
          catchError((error: HttpErrorResponse) => of(programActions.getProgramTiersFailure({ reason: error.error?.response?.message }))),
        ),
      ),
    ),
  );

  public updateProgramTiers$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.updateProgramTiers),
      mergeMap(({ programId, upsertTierDto, loyaltyIntegration, callback }) =>
      forkJoin([
        this.adminService.adminControllerUpdateProgramIntegrationV1({
          programId,
          programIntegrationId: loyaltyIntegration.id,
          updateIntegration: {
            programId,
            integrationId: loyaltyIntegration.integrationId,
            enabledFeatures: loyaltyIntegration.enabledFeatures,
            properties: { ...loyaltyIntegration.properties }
          },
        }),
        this.adminService.adminControllerPatchProgramTiersV1({ programId, upsertTierDto }),
      ]).pipe(
        map(([programIntegration, tiers]) => programActions.updateProgramTiersSuccess({ loyaltyIntegration: programIntegration, tiers, callback })),
        catchError((error: HttpErrorResponse) => of(programActions.updateProgramTiersFailure({ reason: getHttpErrorMessage(error, "TOAST.error.title") }))),
      )),
    ),
  );

  public updateProgramTier$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.updateProgramTier),
      mergeMap(({ programId, tierId, updateTierDto, callback }) =>
        this.adminService.adminControllerUpdateProgramTierV1({ programId, tierId, updateTierDto }).pipe(
          map(tier => programActions.updateProgramTierSuccess({ tier, callback })),
          catchError((error: HttpErrorResponse) => of(programActions.updateProgramTierFailure({ reason: getHttpErrorMessage(error, "TOAST.error.title") }))),
        ),
      ),
    ),
  );

  public disableProgramTiers$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.disableProgramTiers),
      mergeMap(({ loyaltyIntegration, callback }) =>
      forkJoin([
        this.adminService.adminControllerUpdateProgramIntegrationV1({
          programId: loyaltyIntegration.programId,
          programIntegrationId: loyaltyIntegration.id,
          updateIntegration: {
            programId: loyaltyIntegration.programId,
            integrationId: loyaltyIntegration.integrationId,
            enabledFeatures: loyaltyIntegration.enabledFeatures,
            properties: { ...loyaltyIntegration.properties, cycleType: "none", balanceType: "currency" }
          },
        }),
        this.adminService.adminControllerPatchProgramTiersV1({ programId: loyaltyIntegration.programId, upsertTierDto: [] }),
      ]).pipe(
        map(([programIntegration]) => programActions.disableProgramTiersSuccess({ loyaltyIntegration: programIntegration, callback })),
        catchError((error: HttpErrorResponse) => of(programActions.disableProgramTiersFailure({ reason: error.error?.response?.message }))),
      )),
    ),
  );

  public getProgramTierNames$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.getProgramTierNames),
      mergeMap(params =>
        this.adminService.adminControllerGetProgramTierNamesV1(params).pipe(
          map(tierNames => programActions.getProgramTierNamesSuccess({ tierNames })),
          catchError((error: HttpErrorResponse) => of(programActions.getProgramTierNamesFailure({ reason: error.error?.response?.message }))),
        ),
      ),
    ),
  );

  public getIntegrations$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.getIntegrations),
      mergeMap(() =>
        this.adminService.adminControllerGetIntegrationsV1().pipe(
          map(integrations => programActions.getIntegrationsSuccess({ integrations })),
          catchError(() => of(programActions.getIntegrationsFailure())),
        ),
      ),
    ),
  );

  public getProgramIntegrations$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.getProgramIntegrations),
      mergeMap(({ callback, ...params }) =>
        this.programService.programControllerGetProgramIntegrationsV1(params).pipe(
          map(programIntegrations => programActions.getProgramIntegrationsSuccess({ programIntegrations, callback })),
          catchError(() => of(programActions.getProgramIntegrationsFailure())),
        ),
      ),
    ),
  );

  public getAnalytics$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.getAnalytics),
      mergeMap(({ params }) =>
        this.analyticsService.analyticsControllerGetAnalyticsV1(params).pipe(
          map(analytics => programActions.getAnalyticsSuccess({ analytics })),
          catchError(() => of(programActions.getAnalyticsFailure())),
        ),
      ),
    ),
  );

  public getAnalyticsAggregate$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.getAnalyticsAggregate),
      mergeMap(({ params }) =>
        this.analyticsService.analyticsControllerGetAnalyticsAggregateV1(params).pipe(
          map(analyticsAggregate =>
            programActions.getAnalyticsAggregateSuccess({
              analyticsAggregate,
              analyticsAggregateType: params.information,
            }),
          ),
          catchError(() => of(programActions.getAnalyticsAggregateFailure())),
        ),
      ),
    ),
  );

  public getProgramCustomers$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.getProgramCustomers),
      withLatestFrom(this.store.select(fromProgram.selectCustomersPaginationData)),
      mergeMap(([params, { limit }]) => {
        if (limit !== params.limit) {
          params = {
            ...params,
            page: 1,
          };
        }
        return this.adminService.adminControllerGetProgramCustomersV1(params).pipe(
          map(customers => programActions.getProgramCustomersSuccess({ limit: params.limit, customers })),
          catchError(() => of(programActions.getProgramCustomersFailure())),
        );
      }),
    ),
  );

  public getCustomerById$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.getCustomerById),
      mergeMap(params =>
        this.adminService.adminControllerGetCustomerByIdV1(params).pipe(
          map(customer => programActions.getCustomerByIdSuccess({ customer })),
          catchError(() => of(programActions.getCustomerByIdFailure())),
        ),
      ),
    ),
  );

  public getProgramCampaigns$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.getProgramCampaigns),
      mergeMap(({ params }) =>
        this.adminService.adminControllerGetProgramCampaignsV1(params).pipe(
          map(campaigns => programActions.getProgramCampaignsSuccess({ campaigns })),
          catchError(() => of(programActions.getProgramCampaignsFailure())),
        ),
      ),
    ),
  );

  public getProgramLocations$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.getProgramLocations),
      mergeMap(params =>
        this.adminService.adminControllerGetProgramLocationsV1(params).pipe(
          map(locations => programActions.getProgramLocationsSuccess({ locations })),
          catchError(() => of(programActions.getProgramLocationsFailure())),
        ),
      ),
    ),
  );

  public addLocation$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.addLocation),
      mergeMap(({ programId, createLocation, callback }) =>
        this.adminService.adminControllerAddLocationV1({ programId, createLocation }).pipe(
          map(location => programActions.addLocationSuccess({ location, callback })),
          catchError((error: HttpErrorResponse) => of(programActions.addLocationFailure({ formError: getHttpErrorMessage(error, "TOAST.error.title") }))),
        ),
      ),
    ),
  );

  public updateLocation$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.updateLocation),
      mergeMap(({ callback, ...params }) =>
        this.adminService.adminControllerUpdateLocationV1(params).pipe(
          map(location => programActions.updateLocationSuccess({ location, callback })),
          catchError(error => of(programActions.updateLocationFailure({ formError: getHttpErrorMessage(error, "TOAST.error.title") }))),
        ),
      ),
    ),
  );

  public deleteLocation$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.deleteLocation),
      mergeMap(({ programId, locationId }) =>
        this.adminService.adminControllerDeleteLocationV1({ programId, locationId }).pipe(
          map(() => programActions.deleteLocationSuccess({ locationId })),
          catchError(error => of(programActions.deleteLocationFailure({ formError: error?.response?.message }))),
        ),
      ),
    ),
  );

  public deleteLocationSuccess$: Observable<unknown> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.deleteLocationSuccess),
      withLatestFrom(this.store.select(fromRouter.selectRouteParam(AppRouteParams.programSlug))),
      tap(([, programSlug]) => this.router.navigate([programSlug, AppRoutes.settings, AppRoutes.locations])),
    ), { dispatch: false },
  );

  public getProgramDevices$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.getProgramDevices),
      mergeMap(({ params }) =>
        this.adminService.adminControllerGetProgramDevicesV1(params).pipe(
          map(devices => programActions.getProgramDevicesSuccess({ devices })),
          catchError(error => of(programActions.getProgramDevicesFailure({ error: error?.response?.message }))),
        ),
      ),
    ),
  );

  public addDevice$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.addDevice),
      mergeMap(({ programId, createDeviceDto, callback, force }) =>
        this.adminService.adminControllerAddDeviceV1({ programId, createDeviceDto, force }).pipe(
          map(device => programActions.addDeviceSuccess({ device, callback, force })),
          catchError(error =>
            // A device with this serial already exists in the program: ask whether
            // to move/update it in place (handled by addDeviceConflict$).
            !force && isDeviceConflictError(error)
              ? of(programActions.addDeviceConflict({ programId, createDeviceDto, callback }))
              : of(programActions.addDeviceFailure({ error: getHttpErrorMessage(error, "TOAST.error.title") })),
          ),
        ),
      ),
    ),
  );

  public addDeviceConflict$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.addDeviceConflict),
      mergeMap(({ programId, createDeviceDto, callback }) => {
        const dialogRef$ = this.dialogService.open(
          AppDialogs.confirm,
          {
            title: "COMPONENTS.ADD_DEVICE_CONFLICT.title",
            description: "COMPONENTS.ADD_DEVICE_CONFLICT.description",
            cancelText: "COMPONENTS.ADD_DEVICE_CONFLICT.cancel",
            confirmText: "COMPONENTS.ADD_DEVICE_CONFLICT.confirm",
            buttonColor: "dark",
          },
        );

        return dialogRef$.pipe(
          map(result =>
            result?.confirm
              ? programActions.addDevice({ programId, createDeviceDto, callback, force: true })
              : programActions.addDeviceFailure({ error: "" }),
          ),
        );
      }),
    ),
  );

  // These writes run after their dialog has closed, so their errors show over the page (they stay until closed)
  public readonly dialogWriteFailure$: Observable<unknown> = createEffect(() =>
    this.actions$.pipe(
      ofType(
        programActions.addLocationFailure,
        programActions.linkPosToDeviceFailure,
        programActions.createProgramSubscriptionFailure,
      ),
      tap(action => {
        const error = "formError" in action ? action.formError : "reason" in action ? action.reason : action.error;
        this.toast.show({
          title: this.translate.instant("TOAST.error.title"),
          message: error ? this.translate.instant(error) : "",
          type: "error",
          anchor: "viewport",
        });
      }),
    ), { dispatch: false },
  );

  public addDeviceFailure$: Observable<unknown> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.addDeviceFailure),
      filter(({ error }) => !!error),
      tap(({ error }) => this.toast.show({
        title: this.translate.instant(error),
        message: "",
        type: "error",
        anchor: "viewport",
      })),
    ), { dispatch: false },
  );

  public addDeviceForceSuccess$: Observable<unknown> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.addDeviceSuccess),
      filter(({ force }) => !!force),
      tap(() => this.toast.show({
        title: this.translate.instant("TOAST.deviceMoved.title"),
        message: this.translate.instant("TOAST.deviceMoved.message"),
      })),
    ), { dispatch: false },
  );

  public linkPosToDevice$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.linkPosToDevice),
      mergeMap(params =>
        this.adminService.adminControllerLinkPosToDeviceV1(params).pipe(
          map(device => programActions.linkPosToDeviceSuccess({ device, callback: params.callback })),
          catchError(error => of(programActions.linkPosToDeviceFailure({ error: error?.response?.message }))),
        ),
      ),
    ),
  );

  public unlinkPosToDevice$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.unlinkPosToDevice),
      mergeMap(params =>
        this.adminService.adminControllerUnlinkPosFromDeviceV1(params).pipe(
          map(device => programActions.unlinkPosToDeviceSuccess({ device })),
          catchError(error => of(programActions.unlinkPosToDeviceFailure({ error: error?.response?.message }))),
        ),
      ),
    ),
  );

  public linkOrUnlinkPosToDeviceSuccess$: Observable<unknown> = createEffect(() =>
    this.actions$.pipe(
      ofType(
        programActions.linkPosToDeviceSuccess,
        programActions.unlinkPosToDeviceSuccess,
      ),
      tap(({ device }) => this.store.dispatch(programActions.getProgramDevices({ params: { programId: device.programId } }))),
    ), { dispatch: false },
  );

  public deleteDevice$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.deleteDevice),
      mergeMap(params =>
        this.adminService.adminControllerDeleteDeviceV1(params).pipe(
          map(() => programActions.deleteDeviceSuccess({ deviceId: params.deviceId })),
          catchError(error => of(programActions.deleteDeviceFailure({ error: error?.response?.message }))),
        ),
      ),
    ),
  );

  public createCampaign$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.createCampaign),
      mergeMap(({ params, links, pendingCouponBatch, callback }) =>
        (links?.length
          ? forkJoin([
            ...links?.map(link => {
              if (link.id) {
                return this.adminService.adminControllerUpdateLinkV1({
                  linkId: link.id,
                  programId: params.programId,
                  updateLink: {
                    alias: link.alias,
                    label: link.label,
                    url: link.url,
                    programId: params.programId,
                    visibility: "1",
                  },
                });
              } else {
                return this.adminService.adminControllerAddLinkV1({
                  programId: params.programId,
                  createLink: {
                    alias: link.alias,
                    label: link.label,
                    url: link.url,
                    programId: params.programId,
                    visibility: "1",
                  },
                });
              }
            }),
          ])
          : of([])
        ).pipe(
          mergeMap(links => {
            links.forEach(link => this.store.dispatch(programActions.updateCampaignLinkSuccess({ campaignLink: link })));

            const createCouponRequest = [
              CampaignType["Triggered"],
              CampaignType["Anonymous"],
            ].includes(params.createCampaign.type)
              ? this.couponService.couponControllerStoreCouponV1({
                programId: params.programId,
                createCouponDto: { name: params.createCampaign.name },
              })
              : of(null);

            return createCouponRequest
              .pipe(
                // Persist a discount configured before the coupon existed (pending
                // mode). The discount is required to create the campaign, so if the
                // batch or the campaign fails we delete the freshly-created coupon
                // and surface the error instead of leaving an orphan coupon.
                mergeMap(coupon =>
                  (pendingCouponBatch && coupon
                    ? this.couponService.couponControllerCreateCouponBatchV1({
                      programId: params.programId,
                      couponId: coupon.id,
                      createCouponBatchDto: pendingCouponBatch,
                    })
                    : of(null)
                  ).pipe(
                    mergeMap(() => this.adminService.adminControllerCreateCampaignV1({
                      ...params,
                      createCampaign: {
                        ...params.createCampaign,
                        properties: {
                          ...params.createCampaign.properties,

                          notification: params.createCampaign.type === CampaignType["Triggered"]
                            ? {
                              ...params.createCampaign.properties.notification,
                              link: getClaimCodeLink(coupon.slug),
                            }
                            : params.createCampaign.properties.notification,

                          anonymousCampaignProperties: params.createCampaign.type === CampaignType["Anonymous"]
                            ? {
                              ...params.createCampaign.properties.anonymousCampaignProperties,
                              couponId: coupon.id,
                            }
                            : undefined,

                          audience: params.createCampaign.properties.audience
                            ? {
                              platforms: params.createCampaign.properties.audience?.platforms
                                ? params.createCampaign.properties.audience?.platforms?.length > 1
                                  ? [Platforms.all]
                                  : params.createCampaign.properties.audience.platforms
                                : undefined,
                              segments: params.createCampaign.properties.audience?.segments
                                ? params.createCampaign.properties.audience?.segments
                                : undefined,
                            }
                            : undefined,

                          links: links?.length ? links?.map(link => link.id) : undefined,
                        },
                      },
                    })),
                    tap(() => {
                      if (coupon) this.store.dispatch(programActions.getCouponByIdSuccess({ coupon }));
                    }),
                    map(campaign => programActions.createCampaignSuccess({ campaign, callback })),
                    catchError((error: HttpErrorResponse) =>
                      (coupon
                        ? this.couponService.couponControllerDeleteCouponV1({ programId: params.programId, couponId: coupon.id })
                        : of(null)
                      ).pipe(
                        mergeMap(() => of(programActions.createCampaignFailure({ formError: getHttpErrorMessage(error) }))),
                      ),
                    ),
                  ),
                ),
              );
          }),
          catchError((error: HttpErrorResponse) =>
            of(programActions.createCampaignFailure({ formError: getHttpErrorMessage(error) })),
          ),
        ),
      ),
    ),
  );

  // Navigate to the newly created campaign so the route param updates and any
  // page-level *TranslationsService can resolve its campaignId. The success
  // toast is intentionally fired by the page component after save logic (and,
  // where applicable, saveTranslations) resolves — mirroring the reward flow.
  public createCampaignSuccess$: Observable<unknown> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(programActions.createCampaignSuccess),
        withLatestFrom(this.store.select(fromRouter.selectRouteParam(AppRouteParams.programSlug))),
        tap(([{ campaign }, programSlug]) => {
          switch (campaign.type) {
            case CampaignType["Location"]:
              this.router.navigate([programSlug, AppRoutes.campaigns, AppRoutes.locationBased, campaign.id]);
              break;
            case CampaignType["Scheduled"]:
              this.router.navigate([programSlug, AppRoutes.campaigns, AppRoutes.scheduled, campaign.id]);
              break;
            case CampaignType["Links"]:
              this.router.navigate([programSlug, AppRoutes.campaigns, AppRoutes.links, campaign.id]);
              break;
            case CampaignType["Triggered"]:
              this.router.navigate([programSlug, AppRoutes.loyalty, AppRoutes.coupons, campaign.id]);
              break;
            case CampaignType["Anonymous"]:
              this.router.navigate([programSlug, AppRoutes.campaigns, AppRoutes.anonymous, campaign.id]);
              break;
          }
        }),
      ),
    { dispatch: false },
  );

  public updateCampaign$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.updateCampaign),
      mergeMap(({ params, links, suppressToast, correlationId, callback }) =>
        (links?.length
          ? forkJoin([
            ...links?.map(link => {
              if (link.id) {
                return this.adminService.adminControllerUpdateLinkV1({
                  linkId: link.id,
                  programId: params.programId,
                  updateLink: {
                    alias: link.alias,
                    label: link.label,
                    url: link.url,
                    programId: params.programId,
                    visibility: "1",
                  },
                });
              } else {
                return this.adminService.adminControllerAddLinkV1({
                  programId: params.programId,
                  createLink: {
                    alias: link.alias,
                    label: link.label,
                    url: link.url,
                    programId: params.programId,
                    visibility: "1",
                  },
                });
              }
            }),
          ])
          : of([])
        ).pipe(
          mergeMap(links => {
            links.forEach(link => {
              this.store.dispatch(programActions.updateCampaignLinkSuccess({ campaignLink: link }));
            });

            return this.adminService
              .adminControllerUpdateCampaignV1({
                ...params,
                updateCampaign: {
                  ...params.updateCampaign,
                  properties: {
                    ...params.updateCampaign.properties,
                    audience: params.updateCampaign.properties.audience
                      ? {
                        platforms: params.updateCampaign.properties.audience?.platforms
                          ? params.updateCampaign.properties.audience?.platforms?.length > 1
                            ? [Platforms.all]
                            : params.updateCampaign.properties.audience.platforms
                          : undefined,
                        segments: params.updateCampaign.properties.audience?.segments
                          ? params.updateCampaign.properties.audience?.segments
                          : undefined,
                      }
                      : undefined,
                    links: links?.length ? links?.map(link => link.id) : undefined,
                  },
                },
              })
              .pipe(
                map(campaign => programActions.updateCampaignSuccess({ campaign, suppressToast, correlationId, callback })),
                catchError((error: HttpErrorResponse) =>
                  of(programActions.updateCampaignFailure({ formError: getHttpErrorMessage(error), correlationId })),
                ),
              );
          }),
          catchError((error: HttpErrorResponse) =>
            of(programActions.updateCampaignFailure({ formError: getHttpErrorMessage(error), correlationId })),
          ),
        ),
      ),
    ),
  );

  public reactivateCampaign$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.reactivateCampaign),
      mergeMap(({ programId, campaignId, callback }) =>
        this.adminService.adminControllerReactivateCampaignV1({ programId, campaignId }).pipe(
          map(campaign => programActions.updateCampaignSuccess({ campaign, callback })),
          catchError((error: HttpErrorResponse) =>
            of(programActions.updateCampaignFailure({ formError: getHttpErrorMessage(error) })),
          ),
        ),
      ),
    ),
  );

  public deleteCampaign$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.deleteCampaign),
      mergeMap(({ params, callback }) =>
        this.adminService.adminControllerDeleteCampaignV1(params).pipe(
          map(() => programActions.deleteCampaignSuccess({ campaignId: params.campaignId, callback })),
          catchError((error: HttpErrorResponse) =>
            of(programActions.deleteCampaignFailure({ formError: getHttpErrorMessage(error) })),
          ),
        ),
      ),
    ),
  );

  public getBoosts$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.getBoosts),
      mergeMap(({ params, quietFailure }) =>
        this.adminService.adminControllerGetBoostsV1(params).pipe(
          map(boosts => programActions.getBoostsSuccess({ boosts })),
          catchError((error: HttpErrorResponse) => {
            const body = (error.error ?? error) as { message?: string; response?: { message?: string } };
            const fallback = this.translate.instant("BOOSTS_PAGE.errors.loadFailed") as string;
            return of(programActions.getBoostsFailure({ error: body?.message ?? body?.response?.message ?? fallback, quietFailure }));
          }),
        ),
      ),
    ),
  );

  public getBoostsFailure$: Observable<unknown> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(programActions.getBoostsFailure),
        filter(({ quietFailure }) => !quietFailure),
        tap(({ error }) =>
          this.toast.show({
            title: this.translate.instant("TOAST.error.title"),
            message: error ? this.translate.instant(error) : this.translate.instant("BOOSTS_PAGE.errors.loadFailed"),
            type: "error",
            duration: 5000,
          }),
        ),
      ),
    { dispatch: false },
  );

  public createBoost$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.createBoost),
      mergeMap(({ params, callback }) =>
        this.adminService.adminControllerCreateBoostV1(params).pipe(
          map(boost => programActions.createBoostSuccess({ boost, callback })),
          catchError((error: HttpErrorResponse) => of(programActions.createBoostFailure(this.boostFailurePayload(error)))),
        ),
      ),
    ),
  );

  public updateBoost$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.updateBoost),
      mergeMap(({ params, callback }) =>
        this.adminService.adminControllerUpdateBoostV1(params).pipe(
          map(boost => programActions.updateBoostSuccess({ boost, callback })),
          catchError((error: HttpErrorResponse) => of(programActions.updateBoostFailure(this.boostFailurePayload(error)))),
        ),
      ),
    ),
  );

  public archiveBoost$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.archiveBoost),
      mergeMap(({ params, callback }) =>
        this.adminService.adminControllerDeleteBoostV1(params).pipe(
          map(() => programActions.archiveBoostSuccess({ boostId: params.boostId, deletedAt: new Date().toISOString(), callback })),
          catchError((error: HttpErrorResponse) => {
            const body = error.error ?? error;
            return of(programActions.archiveBoostFailure({ formError: body?.message ?? body?.response?.message }));
          }),
        ),
      ),
    ),
  );

  public getCampaignLinks$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.getCampaignLinks),
      mergeMap(params =>
        this.adminService.adminControllerGetProgramLinksV1({ visibility: "1", programId: params.programId }).pipe(
          map(campaignLinks => programActions.getCampaignLinksSuccess({ campaignLinks })),
          catchError(() => of(programActions.getCampaignLinksFailure())),
        ),
      ),
    ),
  );

  public success$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(
        programActions.updateLocationSuccess,
        programActions.updateCampaignSuccess
      ),
      filter(params => !(params as any).suppressToast),
      tap(() => {
        this.toast.show({
          title: this.translate.instant("TOAST.save.title"),
          message: this.translate.instant("TOAST.save.message"),
          anchor: "viewport",
          position: "bottom-right",
        });
      }),
    ),
  { dispatch: false });

  public executeCallBack$: Observable<unknown> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          programActions.createProgramSuccess,
          programActions.createProgramSubscriptionSuccess,
          programActions.persistProgramSuccess,
          programActions.updateProgramSuccess,
          programActions.setProgramLanguagesSuccess,
          programActions.createCampaignSuccess,
          programActions.updateCampaignSuccess,
          programActions.deleteCampaignSuccess,
          programActions.createRewardSuccess,
          programActions.updateRewardSuccess,
          programActions.sendPushSuccess,
          programActions.getProgramIntegrationsSuccess,
          programActions.addProgramIntegrationSuccess,
          programActions.updateProgramIntegrationSuccess,
          programActions.revokeMcpConnectionSuccess,
          programActions.deactivateMcpSuccess,
          programActions.addPointsToCustomerSuccess,
          programActions.removePointsToCustomerSuccess,
          programActions.generateCouponCodeBatchSuccess,
          programActions.linkPosToDeviceSuccess,
          programActions.addDeviceSuccess,
          programActions.addLocationSuccess,
          programActions.updateLocationSuccess,
          programActions.addDomainSuccess,
          programActions.editDomainSuccess,
          programActions.deleteDomainSuccess,
          programActions.deleteApiKeySuccess,
          programActions.updateProgramTiersSuccess,
          programActions.updateProgramTierSuccess,
          programActions.disableProgramTiersSuccess,
          programActions.autoTranslateSectionsSuccess,
          programActions.autoTranslateEarningFlowsSuccess,
          programActions.autoTranslateProfileFormSuccess,
          programActions.upsertProfileFormTranslationsSuccess,
          programActions.createCustomerMetafieldDefinitionSuccess,
          programActions.autoTranslateRewardsSuccess,
          programActions.autoTranslateTiersSuccess,
          programActions.autoTranslateCouponsSuccess,
          programActions.autoTranslateCampaignsSuccess,
          programActions.autoTranslateProgramSuccess,
          programActions.createEngagementSuccess,
          programActions.updateEngagementSuccess,
          programActions.deleteEngagementSuccess,
          programActions.createBoostSuccess,
          programActions.updateBoostSuccess,
          programActions.archiveBoostSuccess,
        ),
        filter(({ callback }) => !!callback),
        tap(({ callback }) => callback()),
      ),
    { dispatch: false },
  );

  public getProgramApiKeys$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.getProgramApiKeys),
      mergeMap(params =>
        this.adminService.adminControllerGetProgramApiKeysV1(params).pipe(
          map(apiKeys => programActions.getProgramApiKeysSuccess({ apiKeys })),
          catchError(() => of(programActions.getProgramApiKeysFailure())),
        ),
      ),
    ),
  );

  public getApiKey$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.getApiKey),
      mergeMap(({ programId, apiKeyId, callback }) =>
        this.adminService.adminControllerGetProgramApiKeyV1({ programId, apiKeyId }).pipe(
          map(apiKey => programActions.getApiKeySuccess({ apiKey, callback })),
          catchError((error: HttpErrorResponse) => of(programActions.getApiKeyFailure({ error: getHttpErrorMessage(error, "PAGE_ERROR.retry") }))),
        ),
      ),
    ),
  );

  public generateApiKey$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.generateApiKey),
      mergeMap(({ callback, ...params }) =>
        this.adminService.adminControllerGenerateApiKeyV1(params).pipe(
          map(apiKey => programActions.generateApiKeySuccess({ apiKey, callback })),
          catchError((error: HttpErrorResponse) => of(programActions.generateApiKeyFailure({ error: getHttpErrorMessage(error, "PAGE_ERROR.retry") }))),
        ),
      ),
    ),
  );

  public updateApiKey$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.updateApiKey),
      mergeMap(({ programId, apiKeyId, updateApiKey, callback }) =>
        this.adminService.adminControllerUpdateApiKeyV1({ programId, apiKeyId, updateApiKey }).pipe(
          map(apiKey => programActions.updateApiKeySuccess({ apiKey, callback })),
          catchError((error: HttpErrorResponse) => of(programActions.updateApiKeyFailure({ error: getHttpErrorMessage(error, "PAGE_ERROR.retry") }))),
        ),
      ),
    ),
  );

  public apiKeyCallback$: Observable<unknown> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(programActions.getApiKeySuccess, programActions.generateApiKeySuccess, programActions.updateApiKeySuccess),
        filter(({ callback }) => !!callback),
        tap(({ apiKey, callback }) => callback(apiKey)),
      ),
    { dispatch: false },
  );

  public deleteApiKey$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.deleteApiKey),
      mergeMap(({ programId, apiKeyId, callback }) =>
        this.adminService.adminControllerDeleteApiKeyV1({ programId, apiKeyId }).pipe(
          map(() => programActions.deleteApiKeySuccess({ apiKeyId, callback })),
          catchError((error: HttpErrorResponse) => of(programActions.deleteApiKeyFailure({ error: getHttpErrorMessage(error, "PAGE_ERROR.retry") }))),
        ),
      ),
    ),
  );

  public addProgramIntegration$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.addProgramIntegration),
      withLatestFrom(this.store.select(fromProgram.selectProgramIntegrations), this.store.select(fromProgram.selectFeatures)),
      mergeMap(([{ programId, createIntegration, integrationName, callback }, stateProgramIntegrations, stateFeatures]) => {
        const programIntegrationFeaturesEnabled: Feature[] = stateFeatures.filter(feature =>
          createIntegration.enabledFeatures.includes(feature.id),
        );

        const affectedIntegrations: ProgramIntegration[] = programIntegrationFeaturesEnabled
          .map(programIntegrationFeature =>
            !programIntegrationFeature.multi
              ? stateProgramIntegrations.find(programIntegration =>
                programIntegration.enabledFeatures.includes(programIntegrationFeature.id),
              )
              : null,
          )
          .filter(Boolean);

        if (affectedIntegrations.length) {
          const affectedFeatures: string = programIntegrationFeaturesEnabled
            .map(programIntegrationFeature => {
              if (!programIntegrationFeature.multi) {
                const affectedIntegration = stateProgramIntegrations.find(programIntegration =>
                  programIntegration.enabledFeatures.includes(programIntegrationFeature.id),
                );

                if (affectedIntegration) {
                  return this.translate.instant(`FEATURES.${programIntegrationFeature.name}`);
                }
              }

              return null;
            })
            .filter(Boolean)
            .join(", ");

          const dialogRef$ = this.dialogService.open(
            AppDialogs.updateIntegrationConfirm,
            {
              description: this.translate.instant("UPDATE_INTEGRATION_CONFIRM.description", { affectedFeatures }),
            },
          );

          return dialogRef$.pipe(
            mergeMap(result => {
              if (result?.confirm) {
                return forkJoin([
                  ...affectedIntegrations.map(affectedIntegration =>
                    this.adminService.adminControllerUpdateProgramIntegrationV1({
                      programId,
                      programIntegrationId: affectedIntegration.id,
                      updateIntegration: {
                        programId,
                        integrationId: affectedIntegration.integrationId,
                        properties: affectedIntegration.properties,
                        enabledFeatures: affectedIntegration.enabledFeatures.filter(enabledFeature => {
                          const feature = stateFeatures.find(feature => feature.id === enabledFeature);
                          return !createIntegration.enabledFeatures.includes(enabledFeature) || feature.multi;
                        }),
                      },
                    }),
                  ),
                ]).pipe(
                  mergeMap(programIntegrations => {
                    const updateSuccessActions = programIntegrations.map(programIntegration => {
                      const integrationName = stateProgramIntegrations.find(pIntegration => pIntegration.id === programIntegration.id)
                        ?.integration?.name;
                      return programActions.updateProgramIntegrationSuccess({
                        integrationName,
                        programIntegration,
                        callback: () => {},
                      });
                    });

                    return this.adminService.adminControllerAddIntegrationV1({ programId, createIntegration }).pipe(
                      mergeMap(programIntegration => [
                        ...updateSuccessActions,
                        programActions.addProgramIntegrationSuccess({ programIntegration, integrationName, callback }),
                      ]),
                      catchError(error => {
                        const formError = getHttpErrorMessage(error, "TOAST.error.title");
                        return of(programActions.addProgramIntegrationFailure({ formError }));
                      }),
                    );
                  }),
                  catchError(error => {
                    const formError = getHttpErrorMessage(error, "TOAST.error.title");
                    return of(programActions.addProgramIntegrationFailure({ formError }));
                  }),
                );
              } else {
                return of(programActions.addProgramIntegrationFailure({ formError: "" }));
              }
            }),
          );
        } else {
          return this.adminService.adminControllerAddIntegrationV1({ programId, createIntegration }).pipe(
            map(programIntegration => programActions.addProgramIntegrationSuccess({ programIntegration, integrationName, callback })),
            catchError(error => {
              const formError = getHttpErrorMessage(error, "TOAST.error.title");
              return of(programActions.addProgramIntegrationFailure({ formError }));
            }),
          );
        }
      }),
    ),
  );

  public updateProgramIntegration$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.updateProgramIntegration),
      withLatestFrom(this.store.select(fromProgram.selectProgramIntegrations), this.store.select(fromProgram.selectFeatures)),
      mergeMap(
        ([{ programId, programIntegrationId, updateIntegration, integrationName, callback }, stateProgramIntegrations, stateFeatures]) => {
          const programIntegration = stateProgramIntegrations.find(programIntegration => programIntegration.id === programIntegrationId);

          const programIntegrationFeaturesDisabled: Feature[] = programIntegration.enabledFeatures
            .map(enabledFeatureId =>
              !updateIntegration.enabledFeatures.includes(enabledFeatureId)
                ? stateFeatures.find(feature => feature.id === enabledFeatureId)
                : null,
            )
            .filter(Boolean);

          const primaryIntegrations = programIntegrationFeaturesDisabled.filter(
            programIntegrationFeatureDisabled => programIntegrationFeatureDisabled.primary,
          );

          if (primaryIntegrations?.length) {
            const dialogRef$ = this.dialogService.open(
              AppDialogs.understood,
              {
                title: "UNDERSTOOD_PAGE.unableToSaveTitle",
                description: this.translate.instant("UNDERSTOOD_PAGE.unableToSaveDescription", {
                  featureName: this.translate.instant(`FEATURES.${primaryIntegrations[0]?.name}`),
                }),
                buttonColor: "transparent",
              },
            );

            return dialogRef$.pipe(map(() => programActions.updateProgramIntegrationFailure({ formError: "" })));
          }

          const programIntegrationFeaturesEnabled: Feature[] = stateFeatures.filter(feature =>
            updateIntegration.enabledFeatures.includes(feature.id),
          );

          const affectedIntegrations: ProgramIntegration[] = programIntegrationFeaturesEnabled
            .map(programIntegrationFeature => {
              if (!programIntegrationFeature.multi) {
                const programIntegration = stateProgramIntegrations.find(programIntegration =>
                  programIntegration.enabledFeatures.includes(programIntegrationFeature.id),
                );

                if (programIntegration?.id !== programIntegrationId) return programIntegration;
              }
              return null;
            })
            .filter(Boolean);

          if (affectedIntegrations.length) {
            const affectedFeatures: string = programIntegrationFeaturesEnabled
              .map(programIntegrationFeature => {
                if (!programIntegrationFeature.multi) {
                  const affectedIntegration = stateProgramIntegrations.find(programIntegration =>
                    programIntegration.enabledFeatures.includes(programIntegrationFeature.id),
                  );

                  if (affectedIntegration) {
                    return this.translate.instant(`FEATURES.${programIntegrationFeature.name}`);
                  }
                }

                return null;
              })
              .filter(Boolean)
              .join(", ");

            const dialogRef$ = this.dialogService.open(
              AppDialogs.updateIntegrationConfirm,
              {
                description: this.translate.instant("UPDATE_INTEGRATION_CONFIRM.description", { affectedFeatures }),
              },
            );

            return dialogRef$.pipe(
              mergeMap(result => {
                if (result?.confirm) {
                  return forkJoin([
                    ...affectedIntegrations.map(affectedIntegration =>
                      this.adminService.adminControllerUpdateProgramIntegrationV1({
                        programId,
                        programIntegrationId: affectedIntegration.id,
                        updateIntegration: {
                          programId,
                          integrationId: affectedIntegration.integrationId,
                          properties: affectedIntegration.properties,
                          enabledFeatures: affectedIntegration.enabledFeatures.filter(enabledFeature => {
                            const feature = stateFeatures.find(feature => feature.id === enabledFeature);
                            return !updateIntegration.enabledFeatures.includes(enabledFeature) || feature.multi;
                          }),
                        },
                      }),
                    ),
                  ]).pipe(
                    mergeMap(programIntegrations => {
                      const updateSuccessActions = programIntegrations.map(programIntegration => {
                        const integrationName = stateProgramIntegrations.find(pIntegration => pIntegration.id === programIntegration.id)
                          ?.integration?.name;
                        return programActions.updateProgramIntegrationSuccess({
                          integrationName,
                          programIntegration,
                          callback: () => {},
                        });
                      });

                      return this.adminService
                        .adminControllerUpdateProgramIntegrationV1({ programId, programIntegrationId, updateIntegration })
                        .pipe(
                          mergeMap(programIntegration => [
                            ...updateSuccessActions,
                            programActions.updateProgramIntegrationSuccess({ programIntegration, integrationName, callback }),
                          ]),
                          catchError(error => {
                            const formError = getHttpErrorMessage(error, "TOAST.error.title");
                            return of(programActions.updateProgramIntegrationFailure({ formError }));
                          }),
                        );
                    }),
                    catchError(error => {
                      const formError = getHttpErrorMessage(error, "TOAST.error.title");
                      return of(programActions.updateProgramIntegrationFailure({ formError }));
                    }),
                  );
                } else {
                  return of(programActions.updateProgramIntegrationFailure({ formError: "" }));
                }
              }),
            );
          } else {
            return this.adminService.adminControllerUpdateProgramIntegrationV1({ programId, programIntegrationId, updateIntegration }).pipe(
              map(programIntegration => programActions.updateProgramIntegrationSuccess({ programIntegration, integrationName, callback })),
              catchError(error => {
                const formError = getHttpErrorMessage(error, "TOAST.error.title");
                return of(programActions.updateProgramIntegrationFailure({ formError }));
              }),
            );
          }
        },
      ),
    ),
  );

  // The Claude (MCP) endpoints are not in the generated client yet: same HttpClient, so the interceptors add the session token
  public getMcpSettings$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.getMcpSettings),
      switchMap(({ programId }) =>
        this.httpClient.get<McpSettings>(this.mcpUrl(programId)).pipe(
          map(mcpSettings => programActions.getMcpSettingsSuccess({ mcpSettings })),
          catchError(() => of(programActions.getMcpSettingsFailure())),
        ),
      ),
    ),
  );

  public revokeMcpConnection$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.revokeMcpConnection),
      mergeMap(({ programId, connectionId, callback }) =>
        this.httpClient.delete(`${this.mcpUrl(programId)}/connections/${encodeURIComponent(connectionId)}`).pipe(
          map(() => programActions.revokeMcpConnectionSuccess({ connectionId, callback })),
          catchError(error => of(programActions.revokeMcpConnectionFailure({ formError: getHttpErrorMessage(error) }))),
        ),
      ),
    ),
  );

  public deactivateMcp$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.deactivateMcp),
      mergeMap(({ programId, callback }) =>
        this.httpClient
          .delete(`${this.integrationService.configuration.basePath}/v1/program/${encodeURIComponent(programId)}/integrations/${MCP_INTEGRATION_NAME}`)
          .pipe(
            map(() => programActions.deactivateMcpSuccess({ callback })),
            catchError(error => of(programActions.deactivateMcpFailure({ formError: getHttpErrorMessage(error) }))),
          ),
      ),
    ),
  );

  public tokenExchange$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.tokenExchange),
      mergeMap(({ programId, tokenExchange }) =>
        this.shopifyControllerTokenExchange({ programId, tokenExchange }).pipe(
          retry(3),
          map(() => programActions.tokenExchangeSuccess()),
          catchError(() => of(programActions.tokenExchangeFailure())),
        ),
      ),
    ),
  );

  // The API reads the Shopify plan during the token exchange, which starts after the program loaded: read it once more
  // when that program did not know it (a new install, or the first visit since the API stores it)
  public readonly refreshShopifyPlan$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.tokenExchangeSuccess),
      withLatestFrom(this.store.select(fromProgram.selectProgram)),
      filter(([, program]) => !!program?.id && program.shopifyPlus == null),
      take(1),
      switchMap(([, program]) =>
        this.programService.programControllerGetProgramV1({ programId: program.id }).pipe(
          map(({ shopifyPlus, shopifyPlanName }) => programActions.setShopifyPlan({ shopifyPlus, shopifyPlanName })),
          catchError(() => EMPTY),
        ),
      ),
    ),
  );

  public sendPush$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.sendPush),
      mergeMap(({ programId, sendPush, callback }) =>
        this.customerActionsService.customerActionsControllerSendPushV1({ programId, sendPush }).pipe(
          map(() => programActions.sendPushSuccess({ callback })),
          catchError(error => {
            const formError = getHttpErrorMessage(error, "TOAST.error.title");
            return of(programActions.sendPushFailure({ formError }));
          }),
        ),
      ),
    ),
  );

  public klaviyoAuthorize$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.klaviyoAuthorize),
      mergeMap(params =>
        this.integrationService.klaviyoControllerAuthorizeV1(params).pipe(
          map(({ redirectUri }) => programActions.klaviyoAuthorizeSuccess({ redirectUri })),
          catchError(() => of(programActions.klaviyoAuthorizeFailure())),
        ),
      ),
    ),
  );

  public klaviyoAuthorizeSuccess$: Observable<unknown> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(programActions.klaviyoAuthorizeSuccess),
        tap(({ redirectUri }) => window.open(redirectUri, "_blank")),
        withLatestFrom(this.store.select(fromProgram.selectProgram)),
        filter(([, program]) => !!program),
        switchMap(([, { id }]) =>
          interval(3000).pipe(
            tap(() => this.store.dispatch(programActions.getProgramIntegrations({ programId: id, isLongPolling: true }))),
            switchMap(() =>
              combineLatest([
                this.store.select(fromProgram.selectProgram),
                this.store.select(fromProgram.selectProgramIntegrationByName(Program.IntegrationEnum.Klaviyo)),
              ]),
            ),
            takeWhile(([_, programIntegration]) => !(programIntegration?.properties as unknown as { accessToken: boolean })?.accessToken),
            take(20),
            finalize(() => {
              this.store.dispatch(programActions.setLoadingKlaviyoAuth({ loadingKlaviyoAuth: false }));
            }),
          ),
        ),
      ),
    { dispatch: false },
  );

  public klaviyoCallback$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.klaviyoCallback),
      mergeMap(params =>
        this.integrationService.klaviyoControllerCallbackV1(params).pipe(
          map(() => programActions.klaviyoCallbackSuccess({ programId: params.programId })),
          catchError(() => of(programActions.klaviyoCallbackFailure())),
        ),
      ),
    ),
  );

  public klaviyoRevoke$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.klaviyoRevoke),
      mergeMap(({ programId }) =>
        this.integrationService.klaviyoControllerRevokeV1({ programId }).pipe(
          map(() => programActions.klaviyoRevokeSuccess({ programId })),
          catchError((error: HttpErrorResponse) => of(programActions.klaviyoRevokeFailure({ reason: error.error.message }))),
        ),
      ),
    ),
  );

  public getFeatures$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.getFeatures),
      mergeMap(() =>
        this.adminService.adminControllerGetFeaturesV1().pipe(
          map(features => programActions.getFeaturesSuccess({ features })),
          catchError(() => of(programActions.getFeaturesFailure())),
        ),
      ),
    ),
  );

  public getSegments$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.getSegments),
      mergeMap(params =>
        this.adminService.adminControllerGetSegmentsV1(params).pipe(
          map(segments => programActions.getSegmentsSuccess({ segments })),
          catchError(() => of(programActions.getSegmentsFailure())),
        ),
      ),
    ),
  );

  public addPointsToCustomer$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.addPointsToCustomer),
      mergeMap(({ programId, customerId, createBooking, callback }) =>
        this.adminService.adminControllerAddPointsToCustomerV1({ programId, customerId, createBooking }).pipe(
          map(customer => programActions.addPointsToCustomerSuccess({ customer, callback })),
          catchError(error => {
            const formError = getHttpErrorMessage(error, "TOAST.error.title");
            return of(programActions.addPointsToCustomerFailure({ formError }));
          }),
        ),
      ),
    ),
  );

  public removePointsToCustomer$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.removePointsToCustomer),
      mergeMap(({ programId, customerId, createBooking, callback }) =>
        this.adminService.adminControllerRemovePointsToCustomerV1({ programId, customerId, createBooking }).pipe(
          map(customer => programActions.removePointsToCustomerSuccess({ customer, callback })),
          catchError(error => {
            const formError = getHttpErrorMessage(error, "TOAST.error.title");
            return of(programActions.removePointsToCustomerFailure({ formError }));
          }),
        ),
      ),
    ),
  );

  public getCustomerEvents$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.getCustomerEvents),
      mergeMap(params =>
        this.adminService.adminControllerGetCustomerEventsV1(params).pipe(
          map(paginatedCustomerEvents =>
            programActions.getCustomerEventsSuccess({ customerId: params.customerId, paginatedCustomerEvents }),
          ),
          catchError(() => of(programActions.getCustomerEventsFailure())),
        ),
      ),
    ),
  );

  public getProgramSubscriptionInfo$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.getProgramSubscriptionInfo),
      mergeMap(({ params, isLongPolling }) =>
        this.adminService.adminControllerGetProgramSubscriptionInfoV1(params).pipe(
          map(subscriptionInfo =>
            programActions.getProgramSubscriptionInfoSuccess({
              subscriptionInfo,
              programId: params.programId,
              isLongPolling: !!isLongPolling,
            }),
          ),
          catchError(() => of(programActions.getProgramSubscriptionInfoFailure())),
        ),
      ),
    ),
  );

  public getProgramSubscriptionInfoSuccess$: Observable<unknown> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(programActions.getProgramSubscriptionInfoSuccess),
        withLatestFrom(this.store.select(fromProgram.selectProgram)),
        // Ignore responses for a program that is no longer loaded (e.g. a cancel
        // poll that resolves after the merchant navigated to another program).
        filter(([{ programId }, program]) => !program || program.id === programId),
        filter(([{ isLongPolling }]) => !isLongPolling),
        tap(([{ subscriptionInfo }, program]) => {
          if (window.posthog) {
            window.posthog.capture("$set", {
              $set: {
                subscriptionStatus: subscriptionInfo.status,
                subscriptionFreePasses: subscriptionInfo.freePasses,
                subscriptionFreeTransactions: subscriptionInfo.freeTransactions,
                subscriptionUsageCharges: subscriptionInfo.usageCharges,
                subscriptionCappedAmount: subscriptionInfo.cappedAmount,
                subscriptionInstalledPasses: subscriptionInfo.installedPasses,
                subscriptionTransactions: subscriptionInfo.transactions,
              },
            });
          }
          if (program) {
            this.intercomService.update({
              company: {
                company_id: program.slug,
                name: program.name,
                plan: this.derivePlan(subscriptionInfo),
                custom_attributes: {
                  program_slug: program.slug,
                  program_environment: environment.name,
                  subscription_status: subscriptionInfo.status ?? "Not active",
                  subscription_installed_passes: subscriptionInfo.installedPasses ?? 0,
                  subscription_free_passes: subscriptionInfo.freePasses ?? 0,
                  subscription_transactions: subscriptionInfo.transactions ?? 0,
                  subscription_free_transactions: subscriptionInfo.freeTransactions ?? 0,
                  subscription_usage_charges: subscriptionInfo.usageCharges ?? 0,
                  subscription_capped_amount: subscriptionInfo.cappedAmount ?? 0,
                },
              },
            });
          }
        }),
      ),
    { dispatch: false },
  );

  public createProgramSubscription$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.createProgramSubscription),
      mergeMap(({ params, navigate, callback }) =>
        this.adminService.adminControllerCreateProgramSubscriptionV1(params).pipe(
          map(redirectLink => programActions.createProgramSubscriptionSuccess({ redirectLink, navigate, callback })),
          catchError((error: HttpErrorResponse) => of(programActions.createProgramSubscriptionFailure({ reason: getHttpErrorMessage(error, "TOAST.error.title") }))),
        ),
      ),
    ),
  );

  public createProgramSubscriptionSuccess$: Observable<unknown> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(programActions.createProgramSubscriptionSuccess),
        tap(({ redirectLink }) => {
          // Embedded, the approval replaces the admin page and Shopify returns to settings/subscription,
          // so polling only applies to the standalone tab.
          window.open(redirectLink?.redirectLink, this.shopifyAppBridgeService.isEmbedded ? "_top" : "_blank");
        }),
        filter(() => !this.shopifyAppBridgeService.isEmbedded),
        withLatestFrom(this.store.select(fromProgram.selectProgram)),
        filter(([, program]) => !!program),
        switchMap(([{ navigate }, { id, slug }]) =>
          interval(3000).pipe(
            tap(() => {
              this.store.dispatch(programActions.getProgramSubscriptionInfo({ params: { programId: id } }));
            }),
            switchMap(() =>
              combineLatest([this.store.select(fromProgram.selectProgram), this.store.select(fromProgram.selectSubscriptionInfo)]),
            ),
            takeWhile(([_, subscriptionInfo]) => subscriptionInfo?.status !== SubscriptionInfo.StatusEnum.Active),
            take(20),
            finalize(() => {
              this.store.dispatch(programActions.setLoadingCreateProgramSubscription({ loadingCreateSubscription: false }));
              if (navigate) this.router.navigate([slug, AppRoutes.design]);
            }),
          ),
        ),
      ),
    { dispatch: false },
  );

  public cancelProgramSubscription$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.cancelProgramSubscription),
      mergeMap(({ params }) =>
        this.adminService.adminControllerCancelProgramSubscriptionV1(params).pipe(
          map(() => programActions.cancelProgramSubscriptionSuccess({ programId: params.programId })),
          catchError(() => of(programActions.cancelProgramSubscriptionFailure())),
        ),
      ),
    ),
  );

  public cancelProgramSubscriptionSuccess$: Observable<unknown> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(programActions.cancelProgramSubscriptionSuccess),
        tap(() => {
          this.toast.show({
            title: this.translate.instant("TOAST.cancelSubscription.title"),
            message: this.translate.instant("TOAST.cancelSubscription.message"),
          });
        }),
        // Poll the program that was canceled, not whichever program is currently
        // loaded -- the merchant may navigate away before the DELETE resolves.
        switchMap(({ programId }) =>
          interval(3000).pipe(
            tap(() => {
              this.store.dispatch(programActions.getProgramSubscriptionInfo({ params: { programId } }));
            }),
            switchMap(() => this.store.select(fromProgram.selectSubscriptionInfo)),
            takeWhile(subscriptionInfo => subscriptionInfo?.status === SubscriptionInfo.StatusEnum.Active),
            take(20),
            // Stop refreshing once the merchant navigates to a different program,
            // so we don't overwrite the newly-loaded program's global subscription state.
            takeUntil(
              this.store.select(fromProgram.selectProgram).pipe(filter(program => !!program && program.id !== programId)),
            ),
            finalize(() => {
              this.store.dispatch(programActions.setLoadingCancelProgramSubscription({ loadingCancelSubscription: false }));
            }),
          ),
        ),
      ),
    { dispatch: false },
  );

  public cancelProgramSubscriptionFailure$: Observable<unknown> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(programActions.cancelProgramSubscriptionFailure),
        tap(() => {
          this.toast.show({
            title: this.translate.instant("TOAST.cancelSubscriptionError.title"),
            message: this.translate.instant("TOAST.cancelSubscriptionError.message"),
            type: "error",
            duration: 5000,
          });
        }),
      ),
    { dispatch: false },
  );

  public getCampaignById$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.getCampaignById),
      mergeMap(params =>
        this.adminService.adminControllerGetCampaignByIdV1(params).pipe(
          map(campaign => programActions.getCampaignByIdSuccess({ campaign })),
          // The editor that asked shows the failure in place of its form (selectCampaignLoadError)
          catchError((error: HttpErrorResponse) =>
            of(programActions.getCampaignByIdFailure({ campaignId: params.campaignId, status: error.status })),
          ),
        ),
      ),
    ),
  );

  public checkCampaignSlug$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.checkCampaignSlug),
      mergeMap(params =>
        this.adminService.adminControllerCheckCampaignSlugV1(params).pipe(
          map(response => programActions.checkCampaignSlugSuccess({ response })),
          catchError(() => of(programActions.checkCampaignSlugFailure())),
        ),
      ),
    ),
  );

  public getCoupons$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.getCoupons),
      concatLatestFrom(() => this.store.select(fromProgram.selectCouponsFilters)),
      concatLatestFrom(() => this.store.select(fromProgram.selectCouponsSearchTerm)),
      mergeMap(([[params, status], searchTerm]) =>
        this.couponService.couponControllerGetCouponsV1({ ...params, status, searchTerm }).pipe(
          map(coupons => programActions.getCouponsSuccess({ limit: params.limit, coupons })),
          catchError(() => of(programActions.getCouponsFailure())),
        ),
      ),
    ),
  );

  public getCouponById$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.getCouponById),
      mergeMap(params =>
        this.couponService.couponControllerGetCouponByIdV1(params).pipe(
          map(coupon => programActions.getCouponByIdSuccess({ coupon })),
          catchError(() => of(programActions.getCouponByIdFailure())),
        ),
      ),
    ),
  );

  public uploadCodesFromCSV$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.generateCouponCodeBatch),
      mergeMap(({ couponId, programId, createCouponBatchDto, callback }) =>
        this.couponService.couponControllerCreateCouponBatchV1({ couponId, programId, createCouponBatchDto }).pipe(
          map(() => programActions.generateCouponCodeBatchSuccess({
            couponId,
            createdFrom: createCouponBatchDto.createdFrom,
            codesRequested: 0,
            generationMode: createCouponBatchDto.generationMode,
            discountType: createCouponBatchDto.discountType,
            value: createCouponBatchDto.value,
            expiresInDays: createCouponBatchDto.expiresInDays,
            callback,
          })),
          // Aborted or network-level failures carry no body, so always fall back
          // to a message: an empty formError leaves the dialog stuck loading.
          catchError(error => of(programActions.generateCouponCodeBatchFailure({
            formError: getHttpErrorMessage(error, "TOAST.couponBatchError.message"),
          }))),
        ),
      ),
    ),
  );

  public removeUnclaimed$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.removeUnclaimed),
      mergeMap(({ programId, couponId, batchId }) =>
        this.couponService.couponControllerRemoveUnclaimedCodesV1({ programId, couponId, batchId }).pipe(
          map(coupon => programActions.removeUnclaimedSuccess({ coupon })),
          catchError(error => of(programActions.removeUnclaimedFailure({ formError: getCouponBatchErrorMessage(error) }))),
        ),
      ),
    ),
  );

  public removeCouponBatch$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.removeCouponBatch),
      mergeMap(({ programId, couponId, batchId }) =>
        this.couponService.couponControllerRemoveCouponBatchV1({ programId, couponId, batchId }).pipe(
          map(coupon => programActions.removeCouponBatchSuccess({ coupon })),
          catchError(error => of(programActions.removeCouponBatchFailure({ formError: getCouponBatchErrorMessage(error) }))),
        ),
      ),
    ),
  );

  public searchCoupons$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.searchCoupons),
      mergeMap(({ programId, searchTerm }) =>
        this.couponService.couponControllerSearchCouponsV1({ programId, searchTerm }).pipe(
          map(results => programActions.searchCouponsSuccess({ results })),
          catchError(error => of(programActions.searchCouponsFailure({ reason: error?.response?.message }))),
        ),
      ),
    ),
  );

  public getCouponStats$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.getCouponStats),
      mergeMap(({ programId, couponId }) =>
        this.couponService.couponControllerGetCouponCountsV1({ programId, couponId }).pipe(
          map(stats => programActions.getCouponStatsSuccess({ couponId, stats })),
          catchError(error => of(programActions.getCouponStatsFailure({ reason: error?.response?.message }))),
        ),
      ),
    ),
  );

  public claimAndDownloadCouponCodes$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.claimAndDownloadCouponCodes),
      mergeMap(({ programId, couponId, claimCouponCodes, callback }) =>
        this.couponService.couponControllerClaimAndDownloadCodesV1(
          {
            programId,
            couponId,
            claimCouponCodes,
          },
          "events",
          false,
          { httpHeaderAccept: "text/csv" as any },
        ).pipe(
          filter(response => response instanceof HttpResponse),
          map((response: any) => programActions.claimAndDownloadCouponCodesSuccess({ csv: response.body, callback })),
          catchError(error => of(programActions.claimAndDownloadCouponCodesFailure({ reason: getHttpErrorMessage(error, "TOAST.error.title") }))),
        ),
      ),
    ),
  );

  public claimAndDownloadCouponCodesSuccess$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.claimAndDownloadCouponCodesSuccess),
      tap(({ csv, callback }) => {
        const anchor = document.createElement("a");
        anchor.setAttribute("href", `data:attachment/csv;charset=utf-8${encodeURI(csv)}`);
        anchor.setAttribute("download", "coupon-codes.csv");
        anchor.click();

        callback();
      }),
    ), { dispatch: false },
  );

  public getProgramRewards$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.getProgramRewards),
      mergeMap(({ programId }) => forkJoin([
        this.couponService.couponControllerGetRewardsV1({ programId }),
        // Only enriches the list with each reward's type and cost, so a failure must not hide it.
        this.couponService.couponControllerGetPublicRewardsV1({ programId }).pipe(catchError(() => of([]))),
      ]).pipe(
        map(([rewards, details]) => programActions.getProgramRewardsSuccess({ rewards, details })),
        catchError(() => of(programActions.getProgramRewardsFailure())),
      )),
    ),
  );

  public getRewardById$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.getRewardById),
      filter(({ rewardId }) => !!rewardId),
      mergeMap(({ programId, rewardId }) => this.couponService.couponControllerGetRewardV1({ programId, rewardId }).pipe(
        map(reward => programActions.getRewardByIdSuccess({ reward })),
        catchError(() => of(programActions.getRewardByIdFailure())),
      )),
    ),
  );

  public createReward$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.createReward),
      mergeMap(({ programId, createRewardDto, pendingCouponBatch, callback }) => this.couponService.couponControllerStoreCouponV1({
        programId,
        createCouponDto: { name: createRewardDto.title },
      }).pipe(
        mergeMap(coupon =>
          (pendingCouponBatch
            ? this.couponService.couponControllerCreateCouponBatchV1({
              programId,
              couponId: coupon.id,
              createCouponBatchDto: pendingCouponBatch,
            })
            : of(null)
          ).pipe(
            mergeMap(() =>
              this.couponService.couponControllerCreateRewardV1({
                programId,
                couponId: coupon.id,
                createRewardDto,
              }),
            ),
            tap(() => this.store.dispatch(programActions.getCouponByIdSuccess({ coupon }))),
            map(reward => programActions.createRewardSuccess({ reward, callback })),
            // The discount is required for new rewards, so if the pending batch
            // or the reward fails we delete the freshly-created coupon and
            // surface the error instead of leaving a reward with no codes.
            catchError(error =>
              this.couponService.couponControllerDeleteCouponV1({
                programId,
                couponId: coupon.id,
              }).pipe(
                mergeMap(() => of(programActions.createRewardFailure({
                  formError: getHttpErrorMessage(error, "Failed to create reward"),
                }))),
              ),
            ),
          ),
        ),
        catchError(error => of(programActions.createRewardFailure({
          formError: getHttpErrorMessage(error, "Failed to create coupon"),
        }))),
      )),
    ),
  );

  // Navigate to the newly created reward so the route param updates and the
  // RewardTranslationsService can resolve its rewardId. The success toast is
  // intentionally fired by the page component after saveTranslations()
  // resolves, so the auto-translate dialog gets a chance to open first.
  public createRewardSuccess$: Observable<unknown> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.createRewardSuccess),
      withLatestFrom(this.store.select(fromRouter.selectRouteParam(AppRouteParams.programSlug))),
      tap(([{ reward }, programSlug]) => {
        this.router.navigate([programSlug, AppRoutes.loyalty, AppRoutes.rewards, reward.id]);
      }),
    ), { dispatch: false },
  );

  public updateReward$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.updateReward),
      mergeMap(({ programId, couponId, rewardId, updateRewardDto, callback }) =>
        this.couponService.couponControllerUpdateRewardV1({ programId, couponId, rewardId, updateRewardDto }).pipe(
          map(reward => programActions.updateRewardSuccess({ reward, callback })),
          catchError(error => of(programActions.updateRewardFailure({ formError: getHttpErrorMessage(error, "TOAST.error.title") }))),
        ),
      ),
    ),
  );

  // The success toast is intentionally fired by the page component after
  // saveTranslations() resolves, so the auto-translate dialog gets a chance
  // to open first.

  public reorderProgramRewards$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.reorderProgramRewards),
      withLatestFrom(
        this.store.select(fromProgram.selectRewards),
        this.store.select(fromProgram.selectProgram),
      ),
      mergeMap(([_, rewards, program]) => {
        const reorderEntitiesDto: ReorderEntitiesDto[] = Object.values(rewards).map((reward, index) => ({ id: reward.id, order: index }));
        return this.couponService.couponControllerReorderRewardsV1({ programId: program.id, reorderEntitiesDto }).pipe(
          map(() => programActions.reorderProgramRewardsSuccess()),
          catchError(error => of(programActions.reorderProgramRewardsFailure({
            reason: error?.response?.message || "Failed to update reward order",
          }))),
        );
      }),
    ),
  );

  public reorderProgramRewardsSuccess$: Observable<unknown> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.reorderProgramRewardsSuccess),
      tap(() => {
        this.toast.show({
          title: this.translate.instant("REWARDS_PAGE.reorderSuccess"),
          message: this.translate.instant("REWARDS_PAGE.reorderSuccessMessage"),
          showAsAbsolute: true,
          type: "success",
          position: "top-left",
        }, ".table");
      }),
    ), { dispatch: false },
  );

  public deleteReward$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.deleteReward),
      mergeMap(({ programId, couponId, rewardId }) => this.couponService.couponControllerDeleteRewardV1({
        programId,
        couponId,
        rewardId,
      }).pipe(
        map(() => programActions.deleteRewardSuccess({ rewardId })),
        catchError(error => of(programActions.deleteRewardFailure({ formError: error?.response?.message }))),
      )),
    ),
  );

  public deleteRewardSuccess$: Observable<unknown> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.deleteRewardSuccess),
      withLatestFrom(this.store.select(fromRouter.selectRouteParam(AppRouteParams.programSlug))),
      tap(([, programSlug]) => {
        this.router.navigate([programSlug, AppRoutes.loyalty, AppRoutes.rewards]);
      }),
    ), { dispatch: false },
  );

  public assignCoupon$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.assignCoupon),
      withLatestFrom(this.store.select(fromRouter.selectRouteParam(AppRouteParams.programSlug))),
      mergeMap(([{ email, campaignSlug }, programSlug]) => this.adminService.adminControllerAssignTriggeredCampaignV1({
        programId: programSlug,
        slug: campaignSlug,
        campaignAssignation: { email },
      }).pipe(
        map(() => programActions.assignCouponSuccess()),
        catchError(error => of(programActions.assignCouponFailure({ error: getHttpErrorMessage(error, "TOAST.error.title") }))),
      )),
    ),
  );

  public assignCouponSuccess$: Observable<unknown> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.assignCouponSuccess),
      tap(() => {
        this.toast.show({
          title: this.translate.instant("TOAST.assignCoupon.title"),
          message: this.translate.instant("TOAST.assignCoupon.message"),
          type: "success",
          position: "bottom-right",
          anchor: "viewport",
        });
      }),
    ), { dispatch: false },
  );

  public assignCouponFailure$: Observable<unknown> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.assignCouponFailure),
      tap(({ error }) => {
        this.toast.show({
          title: this.translate.instant("TOAST.assignCoupon.error"),
          message: this.translate.instant(error),
          type: "error",
          position: "bottom-right",
          anchor: "viewport",
        });
      }),
    ), { dispatch: false },
  );

  public sendLoyaltyCard$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.sendLoyaltyCard),
      withLatestFrom(this.store.select(fromRouter.selectRouteParam(AppRouteParams.programSlug))),
      mergeMap(([{ email, toastSelector }, programSlug]) => this.programService.programControllerSendPassToCustomerV1({
        programId: programSlug,
        checkCustomer: { email },
      }).pipe(
        map(() => programActions.sendLoyaltyCardSuccess({ toastSelector })),
        catchError(error => of(programActions.sendLoyaltyCardFailure({ error: getHttpErrorMessage(error, "TOAST.error.title"), toastSelector }))),
      )),
    ),
  );

  public sendLoyaltyCardSuccess$: Observable<unknown> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.sendLoyaltyCardSuccess),
      tap(({ toastSelector }) => {
        this.toast.show({
          title: this.translate.instant("TOAST.sendLoyaltyCard.title"),
          message: this.translate.instant("TOAST.sendLoyaltyCard.message"),
          showAsAbsolute: true,
          type: "success",
          position: "bottom-center",
          anchor: toastSelector ? "element" : "viewport",
        }, toastSelector);
      }),
    ), { dispatch: false },
  );

  public sendLoyaltyCardFailure$: Observable<unknown> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.sendLoyaltyCardFailure),
      tap(({ error, toastSelector }) => {
        this.toast.show({
          title: this.translate.instant("TOAST.sendLoyaltyCard.error"),
          message: this.translate.instant(error),
          showAsAbsolute: true,
          type: "error",
          position: "bottom-center",
          anchor: toastSelector ? "element" : "viewport",
        }, toastSelector);
      }),
    ), { dispatch: false },
  );

  public sendGiftCard$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.sendGiftCard),
      withLatestFrom(this.store.select(fromRouter.selectRouteParam(AppRouteParams.programSlug))),
      mergeMap(([{ email, campaignSlug }, programSlug]) => this.adminService.adminControllerSendGiftCardV1({
        programId: programSlug,
        campaignId: campaignSlug,
        campaignAssignation: { email, sendEmail: true },
      }).pipe(
        map(() => programActions.sendGiftCardSuccess()),
        catchError(error => of(programActions.sendGiftCardFailure({ error: getHttpErrorMessage(error, "TOAST.error.title") }))),
      )),
    ),
  );

  public sendGiftCardSuccess$: Observable<unknown> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.sendGiftCardSuccess),
      tap(() => {
        this.toast.show({
          title: this.translate.instant("TOAST.sendGiftCard.title"),
          message: this.translate.instant("TOAST.sendGiftCard.message"),
          anchor: "viewport",
          type: "success",
          position: "bottom-right",
        });
      }),
    ), { dispatch: false },
  );

  public sendGiftCardFailure$: Observable<unknown> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.sendGiftCardFailure),
      tap(({ error }) => {
        this.toast.show({
          title: this.translate.instant("TOAST.sendGiftCard.error"),
          message: this.translate.instant(error),
          anchor: "viewport",
          type: "error",
          position: "bottom-right",
        });
      }),
    ), { dispatch: false },
  );

  public getAllowedDomains$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.getAllowedDomains),
      withLatestFrom(this.store.select(fromProgram.selectProgram)),
      mergeMap(([_, program]) => this.adminService.adminControllerGetAllowedDomainsV1({ programId: program.id }).pipe(
        map(allowedDomains => programActions.getAllowedDomainsSuccess({ allowedDomains })),
      )),
    ),
  );

  public validateDomain$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.validateDomain),
      mergeMap(({ programId, domain }) =>
        this.adminService.adminControllerCheckAllowedDomainV1({ programId, createAllowedDomainDto: { domain } })
          .pipe(
            map(({ isAvailable }) => programActions.validateDomainSuccess({ availableDomain: isAvailable })),
            catchError(error => of(programActions.validateDomainFailure({ error: error?.response?.message }))),
          )),
    ),
  );

  public addDomain$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.addDomain),
      withLatestFrom(this.store.select(fromProgram.selectProgram)),
      mergeMap(([{ domain, callback }, program]) =>
        this.adminService.adminControllerCreateAllowedDomainV1({ programId: program.id, createAllowedDomainDto: { domain } }).pipe(
          map(domain => programActions.addDomainSuccess({ domain, callback })),
          catchError(error => of(programActions.addDomainFailure({ error: getHttpErrorMessage(error, "TOAST.error.title") }))),
        ),
      ),
    ),
  );

  public editDomain$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.editDomain),
      withLatestFrom(this.store.select(fromProgram.selectProgram)),
      mergeMap(([{ domain, id, callback }, program]) =>
        this.adminService.adminControllerUpdateAllowedDomainV1({
          programId: program.id,
          allowedDomainId: id,
          updateAllowedDomainDto: { domain },
        }).pipe(
          map(domain => programActions.editDomainSuccess({ domain, callback })),
          catchError(error => of(programActions.editDomainFailure({ error: getHttpErrorMessage(error, "TOAST.error.title") }))),
        ),
      ),
    ),
  );

  public updateDomainSuccess$: Observable<unknown> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.editDomainSuccess, programActions.addDomainSuccess),
      tap(() => {
        this.toast.show({
          title: this.translate.instant("TOAST.save.title"),
          message: this.translate.instant("TOAST.save.message"),
          showAsAbsolute: true,
          type: "success",
          position: "top-right",
        }, "#toastContainerAllowedDomains");
      }),
    ), { dispatch: false },
  );

  public editDomainFailure$: Observable<unknown> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.editDomainFailure, programActions.addDomainFailure),
      tap(({ error }) => {
        this.toast.show({
          title: this.translate.instant("PAGE_ERROR.saveTitle"),
          message: this.translate.instant(error),
          anchor: "viewport",
          type: "error",
          position: "top-right",
        });
      }),
    ), { dispatch: false },
  );

  public deleteDomain$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.deleteDomain),
      withLatestFrom(this.store.select(fromProgram.selectProgram)),
      mergeMap(([{ id, callback }, program]) =>
        this.adminService.adminControllerDeleteAllowedDomainV1({ programId: program.id, allowedDomainId: id }).pipe(
          map(() => programActions.deleteDomainSuccess({ id, callback })),
          catchError(error => of(programActions.deleteDomainFailure({ error: error?.response?.message }))),
        ),
      ),
    ),
  );

  public saveFromOnboarding$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.saveFromOnboarding),
      withLatestFrom(this.store.select(fromProgram.selectProgram)),
      mergeMap(([{ rewards, domainSlug, creditRate, callback }, program]) =>
        this.adminService.adminControllerGetProgramLocationsV1({ programId: program.id }).pipe(
          mergeMap(locations => {
            const { defaultLocale } = getOnboardingProgramLocales(program);
            const locationCampaignCopy = getOnboardingLocationCampaignCopy(defaultLocale);
            const incentivizePurchasesDefaults = ENGAGEMENT_DISPLAY_CONFIG_DEFAULTS[
              EngagementFlowWithProgramEngagementFlowDto.NameEnum.IncentivizePurchases
            ];

            const updateProgramObservable = this.adminService.adminControllerUpdateProgramV1({
              programId: program.id,
              updateProgram: {
                configurations: {
                  ...program.configurations,
                },
              },
            }).pipe(
              map(updatedProgram => ({ updatedProgram })),
              catchError(error => of(programActions.saveFromOnboardingFailure({
                callback,
                error: error?.response?.message || "Failed to update program name",
              }))),
            );

            // Add allowed domain
            const addDomainObservable = this.adminService.adminControllerCreateAllowedDomainV1({
              programId: program.id,
              createAllowedDomainDto: { domain: domainSlug },
            }).pipe(
              map(allowedDomain => ({ allowedDomain })),
              catchError(error => of(programActions.saveFromOnboardingFailure({
                callback,
                error: error?.response?.message || "Failed to create allowed domain",
              }))),
            );

            const getEngagementFlowsObservable = this.adminService.adminControllerGetEngagementFlowsWithProgramEngagementFlowV1({
              programId: program.id,
            }).pipe(
              map(engagements => {
                const incentivizePurchasesFlow = engagements.find(
                  engagement => engagement.name === EngagementFlowWithProgramEngagementFlowDto.NameEnum.IncentivizePurchases,
                );
                return { incentivizePurchasesFlow };
              }),
              catchError(error => of(programActions.saveFromOnboardingFailure({
                callback,
                error: error?.response?.message || "Failed to get engagement flows",
              }))),
            );

            const createEngagementFlowObservable = getEngagementFlowsObservable.pipe(
              mergeMap((result) => {
                if ("error" in result) return of(result);
                const { incentivizePurchasesFlow } = result;
                if (!incentivizePurchasesFlow) {
                  return of(programActions.saveFromOnboardingFailure({
                    callback,
                    error: "Incentivize purchases engagement flow not found",
                  }));
                }

                return this.adminService.adminControllerCreateProgramEngagementFlowV1({
                  programId: program.id,
                  createProgramEngagementFlowDto: {
                    programId: program.id,
                    engagementFlowId: incentivizePurchasesFlow.id,
                    active: true,
                    displayIconUrl: incentivizePurchasesDefaults?.iconUrl,
                    properties: {
                      creditRate: creditRate,
                      roundingMethod: EngagementFlowProperties.RoundingMethodEnum.Round,
                    },
                  },
                }).pipe(
                  map(programEngagementFlow => ({ programEngagementFlow })),
                  catchError(error => of(programActions.saveFromOnboardingFailure({
                    callback,
                    error: error?.response?.message || "Failed to create engagement flow",
                  }))),
                );
              }),
            );

            const rewardCreationObservables = rewards.map(reward => {
              const rewardCopy = getOnboardingRewardCopy({
                nameKey: reward.nameKey,
                value: reward.value,
                redeemCost: reward.redeemCost ?? 0,
                currency: program.currency,
                locale: defaultLocale,
              });

              return this.couponService.couponControllerStoreCouponV1({
                programId: program.id,
                createCouponDto: { name: rewardCopy.title },
              }).pipe(
                mergeMap(coupon =>
                  this.couponService.couponControllerCreateRewardV1({
                    programId: program.id,
                    couponId: coupon.id,
                    createRewardDto: {
                      title: rewardCopy.title,
                      description: rewardCopy.description,
                      redeemCost: reward.redeemCost,
                      order: reward.order,
                      visibilityRules: [],
                      redeemabilityRules: reward.redeemabilityRules || [],
                      image: "",
                    },
                  }).pipe(
                    mergeMap(createdReward =>
                      this.couponService.couponControllerCreateCouponBatchV1({
                        couponId: coupon.id,
                        programId: program.id,
                        createCouponBatchDto: {
                          createdFrom: "shopify",
                          discountType: "fixed",
                          value: reward.value,
                          data: {
                            prefix: "INITIAL",
                            digits: 6,
                            activation: null,
                            expiration: null,
                            uniqueCodes: 5,
                            autoImport: true,
                            minimumRequirement: { type: "subtotal", amount: reward.minimumSpend },
                            target: { type: "all" },
                          },
                        },
                      }).pipe(
                        map(() => ({ reward: createdReward, coupon, rewardInput: reward })),
                        catchError(error =>
                          this.couponService.couponControllerDeleteCouponV1({
                            programId: program.id,
                            couponId: coupon.id,
                          }).pipe(
                            mergeMap(() => of(programActions.saveFromOnboardingFailure({
                              callback,
                              error: error?.response?.message || "Failed to create coupon batch",
                            }))),
                          ),
                        ),
                      ),
                    ),
                    catchError(error => of(programActions.saveFromOnboardingFailure({
                      callback,
                      error: error?.response?.message || "Failed to create reward",
                    }))),
                  ),
                ),
                catchError(error => of(programActions.saveFromOnboardingFailure({
                  callback,
                  error: error?.response?.message || "Failed to create coupon",
                }))),
              );
            });
            const shouldCreateEngagementFlow = creditRate !== null && creditRate !== 0;

            const observables: Observable<SaveFromOnboardingResult>[] = [
              updateProgramObservable,
              addDomainObservable,
              ...(shouldCreateEngagementFlow ? [createEngagementFlowObservable] : []),
              ...rewardCreationObservables,
            ];

            if (locations.length > 0) {
              const locationCampaignObservable = this.adminService.adminControllerCreateCampaignV1({
                programId: program.id,
                createCampaign: {
                  name: locationCampaignCopy.name,
                  active: true,
                  type: CampaignType["Location"],
                  properties: {
                    audience: {
                      segments: ["*"],
                      platforms: [Platforms.all],
                    },
                    locations: locations.map(({ id }) => `location/${id}`),
                    visibility: {
                      fromDate: this.utilsService.formatDateToLocal(new Date()),
                      toDate: this.utilsService.formatDateToLocal(new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)),
                    },
                    relevantText: locationCampaignCopy.body,
                  },
                },
              }).pipe(
                map(campaign => ({ campaign })),
                catchError(error => of(programActions.saveFromOnboardingFailure({
                  callback,
                  error: error?.response?.message || "Failed to create location campaign",
                }))),
              );

              observables.push(locationCampaignObservable);
            }

            return forkJoin(observables).pipe(
              mergeMap(results => {
                const successfulResults = results.filter(
                  (result): result is Exclude<SaveFromOnboardingResult, SaveFromOnboardingFailureResult> =>
                    !this.isSaveFromOnboardingFailureResult(result),
                );

                const updatedProgram = successfulResults
                  .filter(result => "updatedProgram" in result)
                  .map(result => (result).updatedProgram)[0];

                const allowedDomain = successfulResults
                  .filter(result => "allowedDomain" in result)
                  .map(result => (result).allowedDomain)[0];

                const programEngagementFlow = successfulResults
                  .filter(result => "programEngagementFlow" in result)
                  .map(result => (result).programEngagementFlow)[0];

                const rewardResults = successfulResults.filter(result => this.isRewardCreationResult(result));
                const campaignResults = successfulResults.filter(result => this.isCampaignCreationResult(result));
                const translationRows = buildOnboardingTranslationRows({
                  program,
                  programEngagementFlowId: programEngagementFlow?.id,
                  rewards: rewardResults.map(({ reward, coupon, rewardInput }) => ({
                    rewardId: reward.id,
                    couponId: coupon.id,
                    nameKey: rewardInput.nameKey,
                    value: rewardInput.value,
                    redeemCost: rewardInput.redeemCost ?? 0,
                  })),
                  campaignIds: campaignResults.map(({ campaign }) => campaign.id),
                });

                return this.seedOnboardingTranslations(program.id, translationRows).pipe(
                  map(() =>
                    programActions.saveFromOnboardingSuccess({
                      rewards: rewardResults.map(({ reward }) => reward),
                      coupons: rewardResults.map(({ coupon }) => coupon),
                      campaigns: campaignResults.map(({ campaign }) => campaign),
                      updatedProgram,
                      allowedDomain,
                      programEngagementFlow,
                      callback,
                    }),
                  ),
                );
              }),
              catchError(error => of(programActions.saveFromOnboardingFailure({
                callback,
                error: error?.response?.message || "Failed to create rewards and campaign",
              }))),
            );
          }),
          catchError(error => of(programActions.saveFromOnboardingFailure({
            callback,
            error: error?.response?.message || "Failed to get program locations",
          }))),
        ),
      ),
    ),
  );

  public saveFromOnboardingSuccess$: Observable<unknown> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.saveFromOnboardingSuccess),
      withLatestFrom(this.store.select(fromProgram.selectProgram)),
      tap(([{ type, callback, campaigns, rewards, coupons, programEngagementFlow }, program]) => {
        this.track(type, {
          slug: program?.slug,
          contactEmail: program?.contactEmail,
          campaignsCreated: campaigns.length,
          rewardsCreated: rewards.length,
          couponsCreated: coupons.length,
          creditRate: programEngagementFlow?.properties ? `${programEngagementFlow.properties.creditRate * 100}%` : null,
        });
        callback();
      }),
    ), { dispatch: false },
  );

  public saveFromOnboardingProfile$: Observable<unknown> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.saveFromOnboarding),
      withLatestFrom(this.store.select(fromProgram.selectProgram)),
      tap(([{ sector, interests }, program]) => {
        this.track("@program/onboarding-profile", {
          slug: program?.slug,
          sector,
          interests,
        });
      }),
    ), { dispatch: false },
  );

  public trackOnboardingStep$: Observable<unknown> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.trackOnboardingStep),
      tap(({ step, event, properties }) => this.track(`@program/onboarding-step-${event}`, { step, ...properties })),
    ), { dispatch: false },
  );

  public getEngagements$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.getEngagements),
      withLatestFrom(this.store.select(fromProgram.selectProgram)),
      mergeMap(([, program]) =>
        this.adminService.adminControllerGetEngagementFlowsWithProgramEngagementFlowV1({ programId: program.id }).pipe(
          map(engagements => programActions.getEngagementsSuccess({ engagements })),
          catchError(error => of(programActions.getEngagementsFailure({ error: error?.response?.message }))),
        ),
      ),
    ),
  );

  public createEngagement$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.createEngagement),
      withLatestFrom(this.store.select(fromProgram.selectProgram)),
      mergeMap(([{ createProgramEngagementFlowDto, callback }, program]) =>
        this.adminService.adminControllerCreateProgramEngagementFlowV1({ createProgramEngagementFlowDto, programId: program.id }).pipe(
          map(programEngagementFlow => programActions.createEngagementSuccess({ programEngagementFlow, callback })),
          catchError(error => this.engagementFailure$(error, programActions.createEngagementFailure)),
        ),
      ),
    ),
  );

  public updateEngagement$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.updateEngagement),
      withLatestFrom(this.store.select(fromProgram.selectProgram)),
      mergeMap(([{ programEngagementFlowId, updateProgramEngagementFlowDto, callback }, program]) =>
        this.adminService.adminControllerUpdateProgramEngagementFlowV1({
          programId: program.id,
          programEngagementFlowId,
          updateProgramEngagementFlowDto,
        }).pipe(
          map(programEngagementFlow => programActions.updateEngagementSuccess({ programEngagementFlow, callback })),
          catchError(error => this.engagementFailure$(error, programActions.updateEngagementFailure)),
        ),
      ),
    ),
  );


  public deleteEngagement$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.deleteEngagement),
      withLatestFrom(this.store.select(fromProgram.selectProgram)),
      mergeMap(([{ programEngagementFlowId, engagementFlowId, callback }, program]) =>
        this.adminService.adminControllerDeleteProgramEngagementFlowV1({
          programId: program.id,
          programEngagementFlowId,
        }).pipe(
          map(() => programActions.deleteEngagementSuccess({ engagementFlowId, callback })),
          catchError(error => of(programActions.deleteEngagementFailure({ error: getHttpErrorMessage(error, "TOAST.error.title") }))),
        ),
      ),
    ),
  );

  // The success toast is intentionally fired by the page component after
  // saveTranslations() resolves, so the auto-translate dialog gets a chance
  // to open first.

  public readonly trackProgramEvents$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        programActions.persistProgramSuccess,
        programActions.createCampaignSuccess,
        programActions.addProgramIntegrationSuccess,
        programActions.updateProgramIntegrationSuccess,
        programActions.updateLocationSuccess,
        programActions.createRewardSuccess,
        programActions.updateRewardSuccess,
        programActions.sendLoyaltyCardSuccess,
        programActions.sendPushSuccess,
        programActions.addPointsToCustomerSuccess,
        programActions.removePointsToCustomerSuccess,
        programActions.addDomainSuccess,
        programActions.addLocationSuccess,
        programActions.addDeviceSuccess,
        programActions.linkPosToDeviceSuccess,
        programActions.generateApiKeySuccess,
        programActions.assignCouponSuccess,
        programActions.sendGiftCardSuccess,
        programActions.createProgramSubscriptionSuccess,
        programActions.createProgramSuccess,
      ),
      withLatestFrom(this.store.select(fromProgram.selectProgram)),
      tap(([{ type }, program]) => {
        this.track(type, {
          slug: program?.slug,
          contactEmail: program?.contactEmail,
        });
      }),
    ), { dispatch: false },
  );

  public readonly getProgramLanguages$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.getProgramLanguages),
      // Dedupe per-programId: pages that attach multiple translation services
      // (e.g. coupon-campaign attaches both coupon and campaign services)
      // dispatch this action twice synchronously. The in-flight set skips
      // duplicates while a request for the same programId is pending and is
      // pruned by `finalize` when the request settles, so it never grows
      // beyond the currently in-flight programs.
      mergeMap(({ programId }) => {
        if (this.programLanguagesInFlight.has(programId)) return EMPTY;
        this.programLanguagesInFlight.add(programId);
        return this.translationsService.translationControllerGetProgramLanguages({ programId }).pipe(
          map(programLanguages => programActions.getProgramLanguagesSuccess({ programLanguages })),
          catchError((error: HttpErrorResponse) => of(programActions.getProgramLanguagesFailure({ error: error.message }))),
          finalize(() => this.programLanguagesInFlight.delete(programId)),
        );
      }),
    ),
  );

  public readonly setProgramLanguages$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.setProgramLanguages),
      mergeMap(({ programId, setProgramLanguagesDto, toastId, callback }) =>
        this.translationsService.translationControllerSetProgramLanguages({ programId, setProgramLanguagesDto }).pipe(
          map(programLanguages => programActions.setProgramLanguagesSuccess({ programLanguages, toastId, callback })),
          catchError((error: HttpErrorResponse) => of(programActions.setProgramLanguagesFailure({ error: error.message }))),
        ),
      ),
    ),
  );

  public readonly setProgramLanguagesSuccess$: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(programActions.setProgramLanguagesSuccess),
        filter(({ toastId }) => !!toastId),
        tap(({ toastId }) => {
          this.toast.show(
            {
              title: this.translate.instant("TOAST.save.title"),
              message: this.translate.instant("TOAST.save.message"),
              position: "top-right",
              showAsAbsolute: true,
              duration: 1000,
            },
            toastId,
          );
        }),
      ),
    { dispatch: false },
  );

  public readonly getTemplateMetadata$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.getTemplateMetadata),
      switchMap(({ programId }) =>
        this.adminService.adminControllerGetTemplateMetadataV1({ programId }).pipe(
          map(templateMetadata => programActions.getTemplateMetadataSuccess({ templateMetadata })),
          catchError((error: HttpErrorResponse) => of(programActions.getTemplateMetadataFailure({ error: error.message }))),
        ),
      ),
    ),
  );

  public readonly getSectionTranslations$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.getSectionTranslations),
      mergeMap(({ programId, locale, entityType = "section" }) =>
        this.translationsService
          .translationControllerGetTranslations({ programId, locale, entityType })
          .pipe(
            map(translations => programActions.getSectionTranslationsSuccess({ locale, translations, entityType })),
            catchError((error: HttpErrorResponse) => of(programActions.getSectionTranslationsFailure({ error: error.message }))),
          ),
      ),
    ),
  );

  public readonly upsertSectionTranslations$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.upsertSectionTranslations),
      mergeMap(({ programId, upsertTranslationsBulkDto }) =>
        this.translationsService.translationControllerUpsertTranslations({ programId, upsertTranslationsBulkDto }).pipe(
          map(() => programActions.upsertSectionTranslationsSuccess({ translations: upsertTranslationsBulkDto.translations })),
          catchError((error: HttpErrorResponse) => of(programActions.upsertSectionTranslationsFailure({ error: getHttpErrorMessage(error, "TOAST.error.title") }))),
        ),
      ),
    ),
  );

  public readonly autoTranslateSections$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.autoTranslateSections),
      mergeMap(({ programId, autoTranslateDto, callback }) =>
        this.translationsService.translationControllerAutoTranslate({ programId, autoTranslateDto }).pipe(
          map(() => programActions.autoTranslateSectionsSuccess({ callback })),
          catchError((error: HttpErrorResponse) => of(programActions.autoTranslateSectionsFailure({ error: error.message }))),
        ),
      ),
    ),
  );

  public readonly getEarningFlowTranslations$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.getEarningFlowTranslations),
      mergeMap(({ programId, locale }) =>
        this.translationsService
          .translationControllerGetTranslations({ programId, locale, entityType: "earning_flow" })
          .pipe(
            map(translations => programActions.getEarningFlowTranslationsSuccess({ locale, translations })),
            catchError((error: HttpErrorResponse) =>
              of(programActions.getEarningFlowTranslationsFailure({ error: error.message })),
            ),
          ),
      ),
    ),
  );

  public readonly upsertEarningFlowTranslations$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.upsertEarningFlowTranslations),
      mergeMap(({ programId, upsertTranslationsBulkDto }) =>
        this.translationsService.translationControllerUpsertTranslations({ programId, upsertTranslationsBulkDto }).pipe(
          map(() =>
            programActions.upsertEarningFlowTranslationsSuccess({ translations: upsertTranslationsBulkDto.translations }),
          ),
          catchError((error: HttpErrorResponse) =>
            of(programActions.upsertEarningFlowTranslationsFailure({ error: getHttpErrorMessage(error, error.message) })),
          ),
        ),
      ),
    ),
  );

  public readonly autoTranslateEarningFlows$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.autoTranslateEarningFlows),
      mergeMap(({ programId, autoTranslateDto, callback }) =>
        this.translationsService.translationControllerAutoTranslate({ programId, autoTranslateDto }).pipe(
          map(() => programActions.autoTranslateEarningFlowsSuccess({ callback })),
          catchError((error: HttpErrorResponse) =>
            of(programActions.autoTranslateEarningFlowsFailure({ error: error.message })),
          ),
        ),
      ),
    ),
  );

  public readonly getProfileFormTranslations$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.getProfileFormTranslations),
      mergeMap(({ programId, locale }) =>
        forkJoin(
          PROFILE_FORM_TRANSLATION_ENTITY_TYPES.map(entityType =>
            this.translationsService.translationControllerGetTranslations({ programId, locale, entityType }),
          ),
        ).pipe(
          map(translations => programActions.getProfileFormTranslationsSuccess({ locale, translations: translations.flat() })),
          catchError((error: HttpErrorResponse) => of(programActions.getProfileFormTranslationsFailure({ error: error.message }))),
        ),
      ),
    ),
  );

  public readonly upsertProfileFormTranslations$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.upsertProfileFormTranslations),
      mergeMap(({ programId, upsertTranslationsBulkDto, callback }) =>
        this.translationsService.translationControllerUpsertTranslations({ programId, upsertTranslationsBulkDto }).pipe(
          map(() => programActions.upsertProfileFormTranslationsSuccess({ translations: upsertTranslationsBulkDto.translations, callback })),
          catchError((error: HttpErrorResponse) =>
            of(programActions.upsertProfileFormTranslationsFailure({ error: getHttpErrorMessage(error, error.message) })),
          ),
        ),
      ),
    ),
  );

  public readonly autoTranslateProfileForm$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.autoTranslateProfileForm),
      mergeMap(({ programId, autoTranslateDto, callback }) =>
        this.translationsService.translationControllerAutoTranslate({ programId, autoTranslateDto }).pipe(
          map(() => programActions.autoTranslateProfileFormSuccess({ callback })),
          catchError((error: HttpErrorResponse) => of(programActions.autoTranslateProfileFormFailure({ error: error.message }))),
        ),
      ),
    ),
  );

  public readonly getCustomerMetafieldDefinitions$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.getCustomerMetafieldDefinitions),
      switchMap(({ programId }) =>
        this.adminService.profileFormAdminControllerListCustomerMetafieldDefinitionsV1({ programId }).pipe(
          map(definitions => programActions.getCustomerMetafieldDefinitionsSuccess({ definitions })),
          catchError((error: HttpErrorResponse) =>
            of(programActions.getCustomerMetafieldDefinitionsFailure({
              error: getHttpErrorMessage(error, this.translate.instant("COMPLETE_USER_PROFILE_PAGE.form.target.definitionsError")),
            })),
          ),
        ),
      ),
    ),
  );

  public readonly createCustomerMetafieldDefinition$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.createCustomerMetafieldDefinition),
      mergeMap(({ programId, definition, callback }) =>
        this.adminService.profileFormAdminControllerCreateCustomerMetafieldDefinitionV1({
          programId,
          createCustomerMetafieldDefinitionDto: definition,
        }).pipe(
          map(created => programActions.createCustomerMetafieldDefinitionSuccess({ definition: created, callback })),
          catchError((error: HttpErrorResponse) =>
            of(programActions.createCustomerMetafieldDefinitionFailure({ error: this.createMetafieldDefinitionError(error) })),
          ),
        ),
      ),
    ),
  );

  public readonly getRewardTranslations$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.getRewardTranslations),
      mergeMap(({ programId, locale }) =>
        this.translationsService
          .translationControllerGetTranslations({ programId, locale, entityType: "reward" })
          .pipe(
            map(translations => programActions.getRewardTranslationsSuccess({ locale, translations })),
            catchError((error: HttpErrorResponse) =>
              of(programActions.getRewardTranslationsFailure({ error: error.message })),
            ),
          ),
      ),
    ),
  );

  public readonly upsertRewardTranslations$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.upsertRewardTranslations),
      mergeMap(({ programId, upsertTranslationsBulkDto }) =>
        this.translationsService.translationControllerUpsertTranslations({ programId, upsertTranslationsBulkDto }).pipe(
          map(() =>
            programActions.upsertRewardTranslationsSuccess({ translations: upsertTranslationsBulkDto.translations }),
          ),
          catchError((error: HttpErrorResponse) =>
            of(programActions.upsertRewardTranslationsFailure({ error: getHttpErrorMessage(error, error.message) })),
          ),
        ),
      ),
    ),
  );

  public readonly autoTranslateRewards$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.autoTranslateRewards),
      mergeMap(({ programId, autoTranslateDto, callback }) =>
        this.translationsService.translationControllerAutoTranslate({ programId, autoTranslateDto }).pipe(
          map(() => programActions.autoTranslateRewardsSuccess({ callback })),
          catchError((error: HttpErrorResponse) =>
            of(programActions.autoTranslateRewardsFailure({ error: error.message })),
          ),
        ),
      ),
    ),
  );

  public readonly getTierTranslations$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.getTierTranslations),
      mergeMap(({ programId, locale }) =>
        this.translationsService
          .translationControllerGetTranslations({ programId, locale, entityType: "tier" })
          .pipe(
            map(translations => programActions.getTierTranslationsSuccess({ locale, translations })),
            catchError((error: HttpErrorResponse) =>
              of(programActions.getTierTranslationsFailure({ error: error.message })),
            ),
          ),
      ),
    ),
  );

  public readonly upsertTierTranslations$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.upsertTierTranslations),
      mergeMap(({ programId, upsertTranslationsBulkDto }) =>
        this.translationsService.translationControllerUpsertTranslations({ programId, upsertTranslationsBulkDto }).pipe(
          map(() =>
            programActions.upsertTierTranslationsSuccess({ translations: upsertTranslationsBulkDto.translations }),
          ),
          catchError((error: HttpErrorResponse) =>
            of(programActions.upsertTierTranslationsFailure({ error: getHttpErrorMessage(error, error.message) })),
          ),
        ),
      ),
    ),
  );

  public readonly autoTranslateTiers$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.autoTranslateTiers),
      mergeMap(({ programId, autoTranslateDto, callback }) =>
        this.translationsService.translationControllerAutoTranslate({ programId, autoTranslateDto }).pipe(
          map(() => programActions.autoTranslateTiersSuccess({ callback })),
          catchError((error: HttpErrorResponse) =>
            of(programActions.autoTranslateTiersFailure({ error: error.message })),
          ),
        ),
      ),
    ),
  );

  public readonly getCouponTranslations$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.getCouponTranslations),
      mergeMap(({ programId, locale }) =>
        this.translationsService
          .translationControllerGetTranslations({ programId, locale, entityType: "coupon" })
          .pipe(
            map(translations => programActions.getCouponTranslationsSuccess({ locale, translations })),
            catchError((error: HttpErrorResponse) =>
              of(programActions.getCouponTranslationsFailure({ error: error.message })),
            ),
          ),
      ),
    ),
  );

  public readonly upsertCouponTranslations$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.upsertCouponTranslations),
      mergeMap(({ programId, upsertTranslationsBulkDto }) =>
        this.translationsService.translationControllerUpsertTranslations({ programId, upsertTranslationsBulkDto }).pipe(
          map(() =>
            programActions.upsertCouponTranslationsSuccess({ translations: upsertTranslationsBulkDto.translations }),
          ),
          catchError((error: HttpErrorResponse) =>
            of(programActions.upsertCouponTranslationsFailure({ error: error.message })),
          ),
        ),
      ),
    ),
  );

  public readonly autoTranslateCoupons$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.autoTranslateCoupons),
      mergeMap(({ programId, autoTranslateDto, callback }) =>
        this.translationsService.translationControllerAutoTranslate({ programId, autoTranslateDto }).pipe(
          map(() => programActions.autoTranslateCouponsSuccess({ callback })),
          catchError((error: HttpErrorResponse) =>
            of(programActions.autoTranslateCouponsFailure({ error: error.message })),
          ),
        ),
      ),
    ),
  );

  public readonly getCampaignTranslations$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.getCampaignTranslations),
      mergeMap(({ programId, locale }) =>
        this.translationsService
          .translationControllerGetTranslations({ programId, locale, entityType: "campaign" })
          .pipe(
            map(translations => programActions.getCampaignTranslationsSuccess({ locale, translations })),
            catchError((error: HttpErrorResponse) =>
              of(programActions.getCampaignTranslationsFailure({ error: error.message })),
            ),
          ),
      ),
    ),
  );

  public readonly upsertCampaignTranslations$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.upsertCampaignTranslations),
      mergeMap(({ programId, upsertTranslationsBulkDto }) =>
        this.translationsService.translationControllerUpsertTranslations({ programId, upsertTranslationsBulkDto }).pipe(
          map(() =>
            programActions.upsertCampaignTranslationsSuccess({ translations: upsertTranslationsBulkDto.translations }),
          ),
          catchError((error: HttpErrorResponse) =>
            of(programActions.upsertCampaignTranslationsFailure({ error: getHttpErrorMessage(error, error.message) })),
          ),
        ),
      ),
    ),
  );

  public readonly autoTranslateCampaigns$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.autoTranslateCampaigns),
      mergeMap(({ programId, autoTranslateDto, callback }) =>
        this.translationsService.translationControllerAutoTranslate({ programId, autoTranslateDto }).pipe(
          map(() => programActions.autoTranslateCampaignsSuccess({ callback })),
          catchError((error: HttpErrorResponse) =>
            of(programActions.autoTranslateCampaignsFailure({ error: error.message })),
          ),
        ),
      ),
    ),
  );

  public readonly getProgramTranslations$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.getProgramTranslations),
      mergeMap(({ programId, locale }) =>
        this.translationsService
          .translationControllerGetTranslations({ programId, locale, entityType: "program" })
          .pipe(
            map(translations => programActions.getProgramTranslationsSuccess({ locale, translations })),
            catchError((error: HttpErrorResponse) =>
              of(programActions.getProgramTranslationsFailure({ error: error.message })),
            ),
          ),
      ),
    ),
  );

  public readonly upsertProgramTranslations$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.upsertProgramTranslations),
      mergeMap(({ programId, upsertTranslationsBulkDto }) =>
        this.translationsService.translationControllerUpsertTranslations({ programId, upsertTranslationsBulkDto }).pipe(
          map(() =>
            programActions.upsertProgramTranslationsSuccess({ translations: upsertTranslationsBulkDto.translations }),
          ),
          catchError((error: HttpErrorResponse) =>
            of(programActions.upsertProgramTranslationsFailure({ error: getHttpErrorMessage(error, "TOAST.error.title") })),
          ),
        ),
      ),
    ),
  );

  public readonly autoTranslateProgram$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(programActions.autoTranslateProgram),
      mergeMap(({ programId, autoTranslateDto, callback }) =>
        this.translationsService.translationControllerAutoTranslate({ programId, autoTranslateDto }).pipe(
          map(() => programActions.autoTranslateProgramSuccess({ callback })),
          catchError((error: HttpErrorResponse) =>
            of(programActions.autoTranslateProgramFailure({ error: error.message })),
          ),
        ),
      ),
    ),
  );

  public readonly translationsFailure$: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          programActions.getProgramLanguagesFailure,
          programActions.setProgramLanguagesFailure,
          programActions.getSectionTranslationsFailure,
          programActions.upsertSectionTranslationsFailure,
          programActions.autoTranslateSectionsFailure,
          programActions.getEarningFlowTranslationsFailure,
          programActions.upsertEarningFlowTranslationsFailure,
          programActions.autoTranslateEarningFlowsFailure,
          programActions.getProfileFormTranslationsFailure,
          programActions.upsertProfileFormTranslationsFailure,
          programActions.autoTranslateProfileFormFailure,
          programActions.getRewardTranslationsFailure,
          programActions.upsertRewardTranslationsFailure,
          programActions.autoTranslateRewardsFailure,
          programActions.getTierTranslationsFailure,
          programActions.upsertTierTranslationsFailure,
          programActions.autoTranslateTiersFailure,
          programActions.getCouponTranslationsFailure,
          programActions.upsertCouponTranslationsFailure,
          programActions.autoTranslateCouponsFailure,
          programActions.getCampaignTranslationsFailure,
          programActions.upsertCampaignTranslationsFailure,
          programActions.autoTranslateCampaignsFailure,
          programActions.getProgramTranslationsFailure,
          programActions.upsertProgramTranslationsFailure,
          programActions.autoTranslateProgramFailure,
          programActions.getTemplateMetadataFailure,
        ),
        tap(({ error }) =>
          this.toast.show({ title: this.translate.instant("TOAST.error.title"), message: error, type: "error", duration: 5000 }),
        ),
      ),
    { dispatch: false },
  );

  constructor(
    private readonly dialogService: DialogService,
    private readonly actions$: Actions,
    private readonly store: Store<RootState>,
    private readonly programService: ProgramService,
    private readonly adminService: AdminService,
    private readonly analyticsService: AnalyticsService,
    private readonly couponService: CouponService,
    private readonly router: Router,
    private readonly toast: ToastService,
    private readonly translate: TranslateService,
    private readonly integrationService: IntegrationService,
    private readonly shopifyAppBridgeService: ShopifyAppBridgeService,
    private readonly utilsService: UtilsService,
    private readonly httpClient: HttpClient,
    private readonly customerActionsService: CustomerActionsService,
    private readonly translationsService: TranslationsService,
    private readonly intercomService: IntercomService,
  ) {}

  private seedOnboardingTranslations(programId: string, translations: UpsertTranslationDto[]): Observable<unknown> {
    if (translations.length === 0) return of(null);

    return this.translationsService.translationControllerUpsertTranslations({
      programId,
      upsertTranslationsBulkDto: { translations },
    }).pipe(
      catchError((error: unknown) => {
        Sentry.captureException(error, {
          extra: {
            programId,
            translationCount: translations.length,
            source: "onboarding-translations",
          },
        });
        return of(null);
      }),
    );
  }

  private isSaveFromOnboardingFailureResult(result: SaveFromOnboardingResult): result is SaveFromOnboardingFailureResult {
    return "type" in result && result.type === programActions.saveFromOnboardingFailure.type;
  }

  private isRewardCreationResult(
    result: Exclude<SaveFromOnboardingResult, SaveFromOnboardingFailureResult>,
  ): result is { reward: RewardDto; coupon: CouponDto; rewardInput: OnboardingRewardInput } {
    return "reward" in result;
  }

  private isCampaignCreationResult(
    result: Exclude<SaveFromOnboardingResult, SaveFromOnboardingFailureResult>,
  ): result is { campaign: CampaignDto } {
    return "campaign" in result;
  }

  private bootIntercom(program: Program, subscriptionInfo: SubscriptionInfo | null, language: Languages): void {
    this.intercomService.boot({
      app_id: INTERCOM_APP_ID,
      user_id: program.slug,
      email: program.contactEmail,
      name: program.name,
      created_at: program.createdAt ? Math.floor(new Date(program.createdAt).getTime() / 1000) : undefined,
      language_override: language,
      hide_default_launcher: true,
      company: {
        company_id: program.slug,
        name: program.name,
        plan: this.derivePlan(subscriptionInfo),
        custom_attributes: {
          program_slug: program.slug,
          program_environment: environment.name,
          subscription_status: subscriptionInfo?.status ?? "Not active",
          subscription_installed_passes: subscriptionInfo?.installedPasses ?? 0,
          subscription_free_passes: subscriptionInfo?.freePasses ?? 0,
          subscription_transactions: subscriptionInfo?.transactions ?? 0,
          subscription_free_transactions: subscriptionInfo?.freeTransactions ?? 0,
        },
      },
    });
  }

  private track(name: string, properties?: Record<string, unknown>): void {
    if (window.posthog) window.posthog.capture(name, properties);
    this.intercomService.trackEvent(name, properties);
  }

  private derivePlan(subscriptionInfo: SubscriptionInfo | null): IntercomPlan {
    switch (subscriptionInfo?.type) {
      case SubscriptionInfo.TypeEnum.Growth:
        return "Growth";
      case SubscriptionInfo.TypeEnum.Advanced:
        return "Advanced";
      case SubscriptionInfo.TypeEnum.Enterprise:
        return "Enterprise";
      default:
        return "Free";
    }
  }

  private parseDotNotationTranslation(dto: { entityType: string; entityId?: string | null; field: string; locale: string; value: string }, programId: string): EntityTranslation {
    const now = new Date().toISOString();
    const dotIdx = dto.field.indexOf(".");
    const entityId = dotIdx >= 0 ? dto.field.substring(0, dotIdx) : (dto.entityId ?? null);
    const field = dotIdx >= 0 ? dto.field.substring(dotIdx + 1) : dto.field;
    return {
      id: `${dto.entityType}-${entityId}-${field}-${dto.locale}`,
      programId,
      entityType: dto.entityType,
      entityId,
      field,
      locale: dto.locale,
      value: dto.value,
      createdAt: now,
      updatedAt: now,
    };
  }

  private shopifyControllerTokenExchange(requestParameters: ShopifyControllerTokenExchangeV1RequestParams): Observable<string> {
    const tokenExchange = requestParameters.tokenExchange;
    if (tokenExchange === null || tokenExchange === undefined) {
      throw new Error("Required parameter tokenExchange was null or undefined when calling shopifyControllerTokenExchange.");
    }

    const programId = requestParameters.programId;
    if (programId === null || programId === undefined) {
      throw new Error("Required parameter programId was null or undefined when calling shopifyControllerTokenExchange.");
    }

    return this.httpClient.post(
      `${this.integrationService.configuration.basePath}/v1/programs/${encodeURIComponent(String(programId))}/integrations/shopify/token-exchange`,
      tokenExchange,
      { responseType: "text" },
    );
  }

  // The key of a new metafield is taken (with another type) or is one we write ourselves
  private createMetafieldDefinitionError(error: HttpErrorResponse): string {
    const message = getHttpErrorMessage(error);
    if (error.status === HttpStatusCode.Conflict || message === "PROFILE_FORM_METAFIELD_KEY_TAKEN") {
      return this.translate.instant("COMPLETE_USER_PROFILE_PAGE.form.target.create.keyTaken");
    }
    if (message === "PROFILE_FORM_RESERVED_METAFIELD") return this.translate.instant("COMPLETE_USER_PROFILE_PAGE.form.target.create.keyReserved");
    return message || this.translate.instant("TOAST.error.title");
  }

  // A stale custom profile form (saved elsewhere meanwhile) also reloads the flows, so Discard brings the latest one
  private engagementFailure$(
    error: HttpErrorResponse,
    failure: typeof programActions.createEngagementFailure | typeof programActions.updateEngagementFailure,
  ): Observable<Action> {
    const payload = this.engagementFailurePayload(error);
    return payload.profileFormError?.code === "PROFILE_FORM_VERSION_CONFLICT"
      ? of(failure(payload), programActions.getEngagements())
      : of(failure(payload));
  }

  // A rejected custom profile form: its reason in the merchant's words, and the field it is about when the body names one
  private engagementFailurePayload(error: HttpErrorResponse): { error: string; profileFormError?: ProfileFormSaveError } {
    const body = (error?.error ?? error) as { code?: string; message?: string; fieldId?: string; response?: { code?: string; message?: string } };
    const code = [body?.code, body?.response?.code, body?.message, body?.response?.message]
      .find((value): value is ProfileFormSaveErrorCode => PROFILE_FORM_SAVE_ERROR_CODES.includes(value as ProfileFormSaveErrorCode));
    if (!code) return { error: getHttpErrorMessage(error, "TOAST.error.title") };
    return {
      error: this.translate.instant(`COMPLETE_USER_PROFILE_PAGE.form.saveErrors.${code}`),
      profileFormError: { code, fieldId: typeof body?.fieldId === "string" ? body.fieldId : null },
    };
  }

  /** The admin's Claude (MCP) resource of a program: its settings, and its connections below it. */
  private mcpUrl(programId: string): string {
    return `${this.integrationService.configuration.basePath}/v1/program/${encodeURIComponent(programId)}/mcp`;
  }

  private boostFailurePayload(error: HttpErrorResponse): { formError: string; conflictingBoostId?: string } {
    const body = (error.error ?? error) as {
      code?: string;
      message?: string;
      conflictingBoostId?: string;
      response?: { code?: string; message?: string; conflictingBoostId?: string };
    };
    const code = body?.code ?? body?.response?.code;
    const message = body?.message ?? body?.response?.message;
    const conflictingBoostId = body?.conflictingBoostId ?? body?.response?.conflictingBoostId;
    const formError =
      code === "BOOST_INVALID_MULTIPLIERS"
        ? this.translate.instant("BOOSTS_PAGE.errors.invalidMultipliers")
        : (message ?? this.translate.instant("BOOSTS_PAGE.errors.generic"));

    return { formError, conflictingBoostId };
  }
}
